import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { groupSites, groupLabel } from '../lib/siteGroups';
import { getTenderGroupNames } from '../services/tenderWorkflowService';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import {
  Star,
  MapPin,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Zap,
  DollarSign,
  Leaf,
  Building,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Award,
  LayoutGrid,
  Table2,
  Calendar,
  FileText,
  Loader2,
} from 'lucide-react';
import { normalizeEnvironmentalCharges } from '../lib/normalizeCharges';
import { ENV_SCHEMES } from '../lib/envSchemeRegistry';
import { useRequestedContractTerms } from '../hooks/useRequestedContractTerms';
import { getNearestRequestedTerm } from '../utils/offerTermUtils';
import { useDocumentForm } from '../contexts/DocumentFormContext';
import { Offer, ALL_CONTRACT_TERMS, createEmptyEnvironmentalCharges, type EnvironmentalCharges, type EnvironmentalChargeYearData } from '../types/offerTypes';
import { getActiveRetailers, type Retailer } from '../services/retailerService';
import { getOffers } from '../services/tenderApiService';
import { getTenderCosts, calculateTenderCosts, describeSkippedTerms } from '../services/calculationApiService';
import type { ContractCostBreakdown } from '../types/calculation';
import { mapOffersWithSites as mapOffersWithSitesShared } from '../utils/siteOfferMapping';
import { WorkflowNavigation } from './WorkflowNavigation';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { formatEnvRate, formatEnergyRate } from '../lib/ratePrecision';
import { maxPricedYear } from '../lib/pricedYears';
import { formatTermLabel, termYearOf } from '../lib/termLabel';
import { contractWeightOf } from '../services/calculationService';
import { hasRequiredRankOneSelections } from '../lib/rankedSelectionReadiness';
import { isSharedAbnTender, recommendedFlagsForOffers } from '../lib/recommendedFromSelections';

interface CompareAndSelectStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

type ViewMode = 'table' | 'cards';
type SectionKey = 'summary' | 'rates' | 'environmental' | 'nonEnergy' | 'green';

const CompareAndSelectStep: React.FC<CompareAndSelectStepProps> = ({ onNext, onPrevious }) => {
  const { clientData, offers: contextOffers, setOffers, sites, tenderId, selectedSiteId, setSelectedSiteId } = useDocumentForm();

  /**
   * F6 — "Select Group", not "Select Sites".
   *
   * Their words, with the shape they drew:
   *
   *     ✓ H.A. BACHRACH (NOM.) PROPRIETARY LIMITED
   *       NMI: NFFFNRKE264, 40012198944, 40012466169
   *
   * A retailer prices a group as one deal, so the comparison is per group. Picking one selects
   * its first site underneath, which is what the rest of this step already filters on — the
   * offers of every site in a group are the same offers.
   */
  const siteGroups = useMemo(() => groupSites(sites), [sites]);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!tenderId) return;
    let mounted = true;
    getTenderGroupNames(tenderId).then(names => { if (mounted) setGroupNames(names); });
    return () => { mounted = false; };
  }, [tenderId]);

  const selectedGroup = siteGroups.find(g => g.sites.some(st => st.id === selectedSiteId)) ?? siteGroups[0] ?? null;
  const { toast } = useToast();

  /**
   * Tell the user which terms the costing left out, and why.
   *
   * ⚠️ AN OFFER THAT IS SIMPLY ABSENT LOOKS LIKE A RETAILER WHO NEVER QUOTED.
   *
   * The costing drops any term with an unpriced contract year, which is right — averaging over
   * a year nobody priced invents a number. But until now it dropped it in silence, so a
   * reviewer who had typed the rates in themselves saw the offer vanish with no explanation and
   * reported it as "the data didn't calculate". The server has always sent the reason; this is
   * the first thing that reads it.
   *
   * A warning, not an error: the rest of the comparison is valid and is shown.
   */
  const reportSkippedTerms = useCallback((skipped: Parameters<typeof describeSkippedTerms>[0]) => {
    const lines = describeSkippedTerms(skipped);
    if (lines.length === 0) return;
    console.warn('⚠️ Terms omitted from the comparison:', lines);
    toast({
      variant: 'destructive',
      title: lines.length === 1 ? 'An offer was left out' : `${lines.length} offers were left out`,
      description: `${lines.join(' · ')}. Add the missing year's rate and recalculate to include ${lines.length === 1 ? 'it' : 'them'}.`,
    });
  }, [toast]);
  // Standard term lengths (e.g. [12,24,36,48]) offers are requested at — a retailer's
  // non-standard actual term (e.g. 13 months) groups under whichever of these is nearest,
  // for display/filtering only. Cost data and expiry dates still use the real term.
  const requestedTerms = useRequestedContractTerms();
  // When every site shares one ABN (single legal entity), ranking is SHARED across all
  // sites instead of picked independently per site — matches how a single-site tender
  // works. Requires a real, non-empty ABN on every site (an empty/missing ABN can't be
  // trusted to mean "same entity", so that falls back to today's per-site ranking).
  // One copy of this rule, shared with the Recommended Offer step — see isSharedAbnTender.
  const allSitesSameAbn = isSharedAbnTender(sites);

  /**
   * Does a stored selection occupy the rank slot the user is acting on?
   *
   * Shared mode: one set of 3 slots for the whole tender, so every entry competes regardless
   * of which site it was clicked from. Per-site mode: each site has its own 3 slots, so only
   * entries for THIS site compete.
   *
   * ⚠️ This one predicate is used by BOTH the "which rank is taken" check and the "clear this
   * rank" filter. They were previously written separately and disagreed: `usedRanks` counted
   * every entry while the deselect filter only removed entries with no siteId. Selections
   * saved under per-site mode therefore held ranks 1-3 forever once the tender flipped to
   * shared mode (allSitesSameAbn derives from async-loaded `sites`, so it flips mid-session) —
   * the user saw "Maximum 3 offers selected", and unselecting freed nothing because the
   * blocking entries were unreachable. Keep these two readers on this single predicate.
   */
  const inSelectionScope = (
    s: import('../types/offerTypes').RankedOfferSelection,
    siteId: string | undefined,
  ): boolean => (allSitesSameAbn ? true : s.siteId === siteId);

  // LOCAL STATE FOR OFFERS - Persists across navigation
  const [localOffers, setLocalOffers] = useState<Offer[]>([]);
  const hasLoadedRef = useRef(false); // Track if we've attempted to load
  const hasInitializedRef = useRef(false); // Track if we've initialized from context

  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(true);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const hasRemappedRef = useRef(false);
  const [selectedContractYear, setSelectedContractYear] = useState<number | null>(null);
  // No manual Rate Type toggle — each offer card already falls back automatically to
  // whichever rate type it actually has (see getOfferRateTypeInfo's isFallback/actual).
  const selectedRateType: 'Smooth' | 'Stepped' = 'Smooth';
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    summary: true,
    rates: true,
    environmental: true,
    nonEnergy: true,
    green: true,
  });

  // Calculated costs state - from calculation engine
  const [calculatedCosts, setCalculatedCosts] = useState<Map<string, { [term: string]: ContractCostBreakdown }>>(new Map());
  const [availableTerms, setAvailableTerms] = useState<number[]>([]);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);
  const hasFetchedCostsRef = useRef(false);
  const hasTriedFallbackRef = useRef(false);

  // Loading state for Continue button
  const [isContinueLoading, setIsContinueLoading] = useState(false);

  // Ranked offer selections stored on the tender (1st/2nd/3rd choice)
  const [rankedSelections, setRankedSelections] = useState<import('../types/offerTypes').RankedOfferSelection[]>([]);

  // CRITICAL FIX: Initialize local offers from context on mount to prevent empty state flash
  useEffect(() => {
    if (!hasInitializedRef.current && contextOffers.length > 0 && localOffers.length === 0) {
      console.log('✨ CompareAndSelectStep: Initializing local offers from context:', contextOffers.length);
      setLocalOffers(contextOffers);
      hasInitializedRef.current = true;
    }
  }, [contextOffers, localOffers.length]);

  // Use local offers if available, otherwise context offers (these are the RAW DB offers, 1 per retailer)
  const dbOffers = localOffers.length > 0 ? localOffers : contextOffers;

  // OPTION A: Expand each DB offer into multiple "virtual offers" — one per contract term
  // DB stays as 1 row per retailer, but UI shows 1 card per term (like before)
  const offers = React.useMemo(() => {
    const virtualOffers: Offer[] = [];
    dbOffers.forEach(dbOffer => {
      // Derive terms from ALL available sources (union) so no term is missed:
      //  1. extn.contract_terms keys — most reliable (set by offerAutoPopulateService)
      //  2. available_terms DB column — may be stale or incomplete
      //  3. termMonths fallback
      const termSet = new Set<number>();
      const ct = dbOffer.extn?.contract_terms as Record<string, any> | undefined;
      if (ct) {
        Object.keys(ct).forEach(key => {
          const m = parseInt(key.split('_')[0], 10);
          if (!isNaN(m) && m > 0) termSet.add(m);
        });
      }
      if (dbOffer.availableTerms) {
        dbOffer.availableTerms.forEach(t => termSet.add(t));
      }
      const terms = termSet.size > 0
        ? Array.from(termSet).sort((a, b) => a - b)
        : [dbOffer.termMonths];

      terms.forEach(termMonths => {
        const isTermRecommended = (dbOffer.recommendedMonths || []).includes(termMonths);

        virtualOffers.push({
          ...dbOffer,
          id: `${dbOffer.id}__term__${termMonths}`, // virtual ID: parentId + term
          termMonths,
          isRecommended: isTermRecommended,
          recommendedMonths: isTermRecommended ? [termMonths] : [],
          extn: {
            ...(dbOffer.extn || {}),
            parentOfferId: dbOffer.id,
            virtualTermMonths: termMonths,
          },
        });
      });
    });
    return virtualOffers;
  }, [dbOffers]);

  // Map offers with correct siteIds — shared with OfferDetailsStep so both steps agree
  // on the same siteId convention regardless of entry point (normal flow vs edit/regenerate
  // jumping straight to a later step, which skips this component and its remap entirely).
  const mapOffersWithSites = useCallback((offersToMap: Offer[]): Offer[] => {
    return mapOffersWithSitesShared(offersToMap, sites);
  }, [sites]);

  // Reset load flags when tenderId changes
  useEffect(() => {
    hasLoadedRef.current = false;
    hasRemappedRef.current = false;
  }, [tenderId]);

  // Load ranked offer selections from tender on mount / tenderId change
  useEffect(() => {
    if (!tenderId) return;
    const load = async () => {
      const { getRankedOfferSelections } = await import('../services/tenderApiService');
      const result = await getRankedOfferSelections(tenderId);
      if (result.success && result.selections) {
        setRankedSelections(result.selections);
      }
    };
    load();
  }, [tenderId]);

  // Collapse duplicate rank slots once the tender is known to be shared-ABN.
  //
  // ⚠️ Runs as its own effect keyed on allSitesSameAbn, NOT inside the loader above. The
  // loader keys on tenderId and fires while `sites` is still empty, at which point
  // allSitesSameAbn is necessarily false — normalising there would always take the per-site
  // branch and never actually collapse anything.
  //
  // Selections saved before siteId/retailerName were both written can hold one entry per rank
  // PER SITE. Read back as a shared tender (one set of 3 slots) those duplicates occupy every
  // rank, so the user hits "Maximum 3 offers selected" before clicking anything. Keeping the
  // first entry per rank restores three usable slots. Per-site tenders legitimately have one
  // entry per rank per site and are left untouched.
  // Drop selections whose siteId belongs to no site on this tender.
  //
  // ⚠️ These are STALE ROWS FROM AN OLDER SCHEMA, and they silently consume rank slots.
  // ranked_offer_selections.siteId was historically stamped in tender_site JUNCTION-id space,
  // while the offer cards carry the value handleSelectRank writes today. A row in the other id
  // space matches nothing: usedRanks still counts it (so ranks 1-3 read as taken) but the
  // deselect filter can never clear it — the unclearable "Maximum 3 offers selected for this
  // site" seen on prod, where old tenders carry junction-space rows.
  //
  // Selections written by the current code are self-consistent (same value written and
  // compared), so this only ever discards rows the UI could not act on anyway. Runs once sites
  // are loaded — `sites` is empty on mount and everything would look unknown.
  // ⚠️ Also drops selections whose OFFER no longer exists — a second, independent cause of the
  // same "can't unselect, can't pick another" report, and the one actually seen in production.
  //
  // Saving on the Offers step REPLACES the offer rows with new ids. Any rank already pointing at
  // a replaced offer becomes unreachable: the badge still renders from the stored rank, but no
  // card on screen carries that offerId, so there is nothing to click to release it. The user
  // sees an offer marked "2nd Choice" they never picked, and one fewer usable slot.
  //
  // Confirmed on prod tender 8bb9a21f — rank 1 held by an AGL offer that no longer exists, while
  // its siteId was still perfectly valid. That is why the siteId check below could not help it:
  // the two causes are independent and produce an identical symptom.
  useEffect(() => {
    if (sites.length === 0) return;
    const knownSiteIds = new Set(sites.map(s => s.id));
    // Selections store the PARENT (pre-grouping) offer id, so accept both that and the row id.
    const knownOfferIds = new Set<string>();
    dbOffers.forEach(o => {
      if (o.id) knownOfferIds.add(String(o.id));
      const parentId = (o.extn as Record<string, unknown> | undefined)?.parentOfferId;
      if (parentId) knownOfferIds.add(String(parentId));
    });

    setRankedSelections(prev => {
      // Only prune offers once they have actually loaded — dbOffers is empty on first render and
      // every selection would look stale.
      const canCheckOffers = knownOfferIds.size > 0;
      const usable = prev.filter(s =>
        (!s.siteId || knownSiteIds.has(s.siteId)) &&
        (!canCheckOffers || !s.offerId || knownOfferIds.has(s.offerId))
      );
      if (usable.length === prev.length) return prev; // no-op — keep identity, avoid a re-render loop
      console.warn(`[ranked] dropped ${prev.length - usable.length} selection(s) referencing sites or offers not on this tender — rank slots freed`);
      return usable;
    });
  }, [sites, dbOffers]);

  useEffect(() => {
    if (!allSitesSameAbn) return;
    setRankedSelections(prev => {
      const collapsed = prev.filter((s, i, all) => all.findIndex(o => o.rank === s.rank) === i);
      if (collapsed.length === prev.length) return prev; // no-op — keep identity, avoid a re-render loop
      console.warn(`[ranked] collapsed ${prev.length} stored selections to ${collapsed.length} for shared-ABN tender`);
      return collapsed;
    });
  }, [allSitesSameAbn]);

  // CRITICAL: Load offers from DB - ONLY when sites are available
  useEffect(() => {
    const loadOffers = async () => {
      // CRITICAL: Wait for sites to be loaded before attempting to load offers
      if (sites.length === 0) {
        console.log('⏳ CompareAndSelectStep: Waiting for sites to load...');
        return;
      }

      // Only load from DB if tenderId exists
      if (!tenderId) return;

      // Prevent multiple simultaneous loads
      if (hasLoadedRef.current) return;

      try {
        hasLoadedRef.current = true;
        setIsLoadingOffers(true);
        console.log('🔄 CompareAndSelectStep: Loading offers from DB');
        const result = await getOffers(tenderId);

        if (result.success && result.offers && result.offers.length > 0) {
          // Map offers with correct siteIds now that sites are available
          const mappedOffers = mapOffersWithSites(result.offers);

          console.log(`✅ CompareAndSelectStep: Loaded ${mappedOffers.length} offers from DB`);
          setLocalOffers(mappedOffers); // Store in LOCAL STATE
          setOffers(mappedOffers); // Also update context
        } else {
          console.log('📭 CompareAndSelectStep: No offers found in DB');
        }
      } catch (error) {
        console.error('❌ CompareAndSelectStep: Error loading offers:', error);
        hasLoadedRef.current = false; // Reset on error so we can retry
      } finally {
        setIsLoadingOffers(false);
      }
    };

    loadOffers();
  }, [tenderId, sites.length, setOffers, mapOffersWithSites]); // Depend on sites.length to wait for sites

  // CRITICAL: Re-map offers when sites become available (if offers already loaded)
  // NOTE: localOffers intentionally excluded from deps to prevent infinite loop.
  // We only need to re-map when sites change, and we read latest localOffers at execution time.
  useEffect(() => {
    if (hasRemappedRef.current) return;
    hasRemappedRef.current = true;

    if (localOffers.length > 0 && sites.length > 0) {
      const needsRemapping = localOffers.some(offer => {
        const matchingSite = sites.find(s => s.id === offer.siteId);
        return !matchingSite;
      });

      if (needsRemapping) {
        console.log('🔄 CompareAndSelectStep: Re-mapping offers with newly loaded sites');
        const remappedOffers = mapOffersWithSites(localOffers);
        setLocalOffers(remappedOffers);
        setOffers(remappedOffers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites.length]);

  // Auto-select first site if no site selected and offers exist
  useEffect(() => {
    if (sites.length > 0 && offers.length > 0) {
      if (!selectedSiteId) {
        // Auto-select the site of the first offer, or first site
        const firstOfferSiteId = offers[0]?.siteId;
        if (firstOfferSiteId && sites.find(s => s.id === firstOfferSiteId)) {
          setSelectedSiteId(firstOfferSiteId);
        } else {
          setSelectedSiteId(sites[0].id);
        }
      } else {
        // Validate existing selection
        const siteExists = sites.find(s => s.id === selectedSiteId);
        if (!siteExists) {
          const firstOfferSiteId = offers[0]?.siteId;
          if (firstOfferSiteId && sites.find(s => s.id === firstOfferSiteId)) {
            setSelectedSiteId(firstOfferSiteId);
          } else {
            setSelectedSiteId(sites[0].id);
          }
        }
      }
    }
  }, [sites.length, offers.length, selectedSiteId, setSelectedSiteId]);

  // Fetch retailers from database
  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        setLoadingRetailers(true);
        const result = await getActiveRetailers();
        if (result.success && result.data) {
          setRetailers(result.data);
        }
      } catch (error) {
        console.error('Error fetching retailers:', error);
      } finally {
        setLoadingRetailers(false);
      }
    };
    fetchRetailers();
  }, []);

  // Fetch calculated costs for all offers in this tender
  useEffect(() => {
    // Reset flags at the START of this effect (before the async call begins).
    // Previously these were reset in a SEPARATE effect with the same [tenderId] dep,
    // which ran AFTER this one and erased hasFetchedCostsRef right after we set it.
    hasFetchedCostsRef.current = false;
    hasTriedFallbackRef.current = false;

    const fetchCalculatedCosts = async () => {
      if (!tenderId || hasFetchedCostsRef.current) return;

      try {
        setIsLoadingCosts(true);
        hasFetchedCostsRef.current = true;
        console.log('🧮 CompareAndSelectStep: Fetching calculated costs for tender:', tenderId);

        const result = await getTenderCosts(tenderId);

        if (result.success && result.data) {
          console.log(`✅ Loaded costs for ${result.data.offers.length} offers, terms: ${result.data.terms_available}`);
          console.log('📊 DEBUG - Raw API response offers:', result.data.offers.map(o => ({
            offer_id: o.offer_id,
            has_calculations: o.has_calculations,
            costs_by_term_keys: Object.keys(o.costs_by_term),
            sample_perYear: o.costs_by_term['48']?.perYear || o.costs_by_term['12']?.perYear || 'N/A'
          })));

          // Build costs map: offer_id -> { "12": breakdown, "24": breakdown, ... }
          const costsMap = new Map<string, { [term: string]: ContractCostBreakdown }>();
          result.data.offers.forEach((offer) => {
            if (offer.has_calculations && Object.keys(offer.costs_by_term).length > 0) {
              costsMap.set(offer.offer_id, offer.costs_by_term);
              console.log(`📌 Added to costsMap: offer_id="${offer.offer_id}", terms=${Object.keys(offer.costs_by_term).join(',')}`);
            }
          });

          // Derive available terms from actual cost_data keys (most accurate)
          // rather than relying solely on terms_available which may lag
          const allTermsSet = new Set<number>();
          costsMap.forEach(termMap => {
            Object.keys(termMap).forEach(t => {
              const n = parseInt(t, 10);
              if (!isNaN(n) && n > 0) allTermsSet.add(n);
            });
          });
          // Also include terms_available as a fallback
          result.data.terms_available.forEach(t => allTermsSet.add(t));
          const derivedTerms = Array.from(allTermsSet).sort((a, b) => a - b);

          console.log(`📊 costsMap built with ${costsMap.size} entries, terms: ${derivedTerms}`);
          setCalculatedCosts(costsMap);
          setAvailableTerms(derivedTerms);
        } else {
          console.log('📭 No calculated costs found. Error:', result.error);
        }
      } catch (error) {
        console.error('❌ Error fetching calculated costs:', error);
      } finally {
        setIsLoadingCosts(false);
      }
    };

    fetchCalculatedCosts();
  }, [tenderId]);

  // FALLBACK: If main fetch returned 0 costs but we have offers, trigger calculation then reload.
  useEffect(() => {
    const runFallback = async () => {
      // Only run if: main fetch finished (not loading), got no results, have offers, haven't tried fallback
      // NOTE: isLoadingCosts covers the "main fetch in progress" case — hasFetchedCostsRef is not
      // used here because it gets reset inside the fetch effect on mount (before async completes).
      if (isLoadingCosts || calculatedCosts.size > 0 || dbOffers.length === 0 || hasTriedFallbackRef.current) return;

      console.log('⚠️ CompareAndSelectStep: No pre-computed costs found. Triggering calculation for', dbOffers.length, 'offers');
      hasTriedFallbackRef.current = true;
      setIsLoadingCosts(true);

      try {
        if (!tenderId) return;

        // TRIGGER calculation first (stores results in offer_cost_breakdown)
        const calcResult = await calculateTenderCosts(tenderId);
        console.log(`🧮 Fallback calc: calculated=${calcResult.calculated}, failed=${calcResult.failed}`);
        reportSkippedTerms(calcResult.skippedTerms);

        // Then re-fetch the now-computed costs
        const result = await getTenderCosts(tenderId);
        if (result.success && result.data) {
          const newMap = new Map<string, { [term: string]: ContractCostBreakdown }>();
          const allTerms = new Set<number>();

          result.data.offers.forEach((offer) => {
            if (offer.has_calculations && Object.keys(offer.costs_by_term).length > 0) {
              newMap.set(offer.offer_id, offer.costs_by_term);
              Object.keys(offer.costs_by_term).forEach(t => {
                const n = parseInt(t, 10);
                if (!isNaN(n) && n > 0) allTerms.add(n);
              });
            }
          });
          result.data.terms_available.forEach(t => allTerms.add(t));

          if (newMap.size > 0) {
            console.log('✅ Fallback: Loaded costs after calculation for', newMap.size, 'offers');
            setCalculatedCosts(newMap);
            if (allTerms.size > 0) setAvailableTerms(Array.from(allTerms).sort((a, b) => a - b));
          }
        }
      } catch (e) {
        console.error('❌ Fallback calculation failed:', e);
      } finally {
        setIsLoadingCosts(false);
      }
    };

    runFallback();
  }, [offers.length, isLoadingCosts, calculatedCosts.size, tenderId]);

  // NOTE: hasFetchedCostsRef and hasTriedFallbackRef are reset inside the
  // fetchCalculatedCosts effect above (at the top, before the async call).
  // A separate reset effect here would run AFTER the fetch starts and erase the guard.

  // Resolve virtual offer ID to real DB offer ID.
  // Virtual offers use the pattern "parentId__term__N" — strip the suffix to get the real ID.
  const resolveOfferId = (id: string): string =>
    id.includes('__term__') ? id.split('__term__')[0] : id;

  // Helper to get calculated annual cost for an offer
  // Uses sites[*].total (correct) instead of perYear (buggy in cost_data)
  const getCalculatedAnnualCost = useCallback((offerId: string, termMonths?: number): number | null => {
    const realId = resolveOfferId(offerId);
    const offerCosts = calculatedCosts.get(realId);
    if (!offerCosts) return null;

    const getFromBreakdown = (bd: ContractCostBreakdown): number | null => {
      const siteCosts = getSiteAggregatedCosts(bd);
      if (siteCosts) return siteCosts.annualOfferCost;
      return bd.perYear || null;
    };

    if (termMonths) {
      const t = termMonths.toString();
      const preferred  = selectedRateType === 'Smooth' ? `${t}_smooth` : `${t}_stepped`;
      const secondary  = selectedRateType === 'Smooth' ? `${t}_stepped` : `${t}_smooth`;
      const bd = offerCosts[preferred] || offerCosts[t] || offerCosts[secondary];
      if (bd) return getFromBreakdown(bd);
    }

    const firstTerm = Object.keys(offerCosts)[0];
    if (firstTerm) return getFromBreakdown(offerCosts[firstTerm]);

    return null;
  }, [calculatedCosts, selectedRateType]);

  // Helper to get full cost breakdown for an offer
  const getCalculatedBreakdown = useCallback((offerId: string, termMonths: number): ContractCostBreakdown | null => {
    const realId = resolveOfferId(offerId);
    const offerCosts = calculatedCosts.get(realId);
    if (!offerCosts) return null;

    const t = termMonths.toString();
    const preferred = selectedRateType === 'Smooth' ? `${t}_smooth` : `${t}_stepped`;
    const secondary = selectedRateType === 'Smooth' ? `${t}_stepped` : `${t}_smooth`;
    return offerCosts[preferred] || offerCosts[t] || offerCosts[secondary] || null;
  }, [calculatedCosts, selectedRateType]);

  // selectedContractYear holds a CONTRACT YEAR (1..6), matched against the year each offer's
  // real term resolves to. Previously both sides snapped to the nearest requested term, which
  // ignored the reviewer's assignment and grouped two distinct terms together. Cost-data
  // lookups elsewhere (getCalculatedBreakdown etc.) keep using the real termMonths, unaffected.
  const filteredOffers = isLoadingOffers
    ? offers
    : offers.filter(offer => {
      const matchesContractTerm = !selectedContractYear
        || termYearOf(offer.termMonths) === selectedContractYear;
      const matchesSite = !selectedSiteId || offer.siteId === selectedSiteId;
      return matchesSite && matchesContractTerm;
    });

  // Contract-term filter options, as CONTRACT YEARS.
  //
  // These used to be month buckets: every actual term was snapped to its nearest requested
  // term (12/24/36/48) and deduped. That silently discarded the reviewer's assignment and
  // could merge two real terms into one entry — an offer quoting 4/16/28/40/52 snapped to
  // {12,24,36,48} because 4 and 16 both round to 12, so the dropdown showed Years 1-4 and the
  // term assigned to Year 5 had nowhere to appear at all.
  //
  // Grouping by the year each term actually resolves to (termYearOf — the assignment when
  // there is one, else the derived number) is the same thing the offer cards, the term pages
  // and the generated proposal already show, so the filter can no longer disagree with them.
  const contractYearOptions = React.useMemo(() => {
    const source = availableTerms.length > 0 ? availableTerms : [...ALL_CONTRACT_TERMS];
    const years = new Set<number>(source.map(t => termYearOf(t)));
    return Array.from(years).sort((a, b) => a - b);
  }, [availableTerms]);


  // Name the chosen bucket the way the rest of the app names it — "Year 1", not "12-month".
  //
  // The dropdown groups by the nearest REQUESTED term, so a 16-month offer belongs under Year 1
  // and is meant to be read as a Year 1 offer. Stating a month count named a length that often
  // no offer on screen actually had ("Showing 12-month offers only" above a 16-month offer), and
  // stating the real 16 would contradict the grouping. The year label is true either way, and
  // matches the dropdown, the term pages and the generated proposal, which all use formatTermLabel.
  const shownTermLabel = React.useMemo(
    () => (selectedContractYear ? `Year ${selectedContractYear}` : null),
    [selectedContractYear],
  );

  // Get selected site
  const selectedSite = sites.find(s => s.id === selectedSiteId);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format rate
  const formatRate = (rate: number): string => {
    // Full quoted precision, never 2 dp — see lib/ratePrecision. AGL quotes 9.9076 c/kWh and
    // rounding it to 9.91 both misreports the rate and, where a cost column is derived from the
    // displayed figure, moves the money (62,740 kWh x 9.91 = $6,217.53 against a true $6,215.99).
    return `${formatEnergyRate(rate * 100)}c/kWh`;
  };

  // Format percentage
  const formatPercent = (percent: number): string => {
    return `${percent.toFixed(2)}%`;
  };

  // Build siteIdMap from tender_site records.
  // If no tender_site records exist for this tender (e.g. tender was created via URL, not OffersStep),
  // auto-create them so saves are not silently skipped.
  const buildSiteIdMap = async (
    orgId: string,
    saveTenderSitesFn: (data: any) => Promise<any>,
  ): Promise<Map<string, string>> => {
    const siteIdMap = new Map<string, string>();

    const { data: tenderSites } = await supabase
      .from('tender_site')
      .select('id, site_id, nmi')
      .eq('tender_id', tenderId);

    if (tenderSites && tenderSites.length > 0) {
      tenderSites.forEach(ts => {
        // Match by site_id first
        if (ts.site_id) {
          const matchingSite = sites.find(s => s.id === ts.site_id);
          if (matchingSite) {
            siteIdMap.set(matchingSite.id, ts.id);
            return;
          }
        }
        // Fallback: match by NMI (tender_site.site_id may be null)
        if (ts.nmi) {
          const matchingSite = sites.find(s => s.nmi === ts.nmi);
          if (matchingSite) siteIdMap.set(matchingSite.id, ts.id);
        }
      });
    }

    // If still no match, create tender_site records now for all context sites
    if (siteIdMap.size === 0 && sites.length > 0) {
      console.log('⚠️ No tender_site records found for tender, creating them now...');
      const result = await saveTenderSitesFn({
        orgId,
        tenderId,
        sites: sites.map(s => ({ id: s.id, name: s.name, nmi: s.nmi })),
      });

      if (result.success && result.siteIdMap) {
        result.siteIdMap.forEach((tenderSiteId: string, siteId: string) => {
          siteIdMap.set(siteId, tenderSiteId);
        });
        console.log(`✅ Created tender_site records, siteIdMap size: ${siteIdMap.size}`);
      } else {
        console.error('❌ Failed to create tender_site records:', result.error);
      }
    }

    return siteIdMap;
  };

  // Handle selecting/unselecting a ranked offer (rank 1, 2, or 3).
  // Two modes:
  //  - Per-site (default, different ABN per site): scoped to the site the clicked offer
  //    belongs to. Each site gets its OWN independent rank 1/2/3.
  //  - Shared (allSitesSameAbn — one legal entity across every site): ONE rank 1/2/3
  //    applies to every site, matched by retailer+term rather than one site's specific
  //    offer row, since each site still has its own sibling offer row for that retailer.
  // Rankings are stored on the tender (ranked_offer_selections JSONB), not on the offer itself.
  // siteId is captured on the selection itself at click time — never re-derived later by
  // looking up the offer, so this stays correct even if the offers list gets reloaded/remapped.
  // Rank 1 also sets is_recommended / recommended_months on the offer for backward compat.
  const handleSelectRank = async (offerId: string, rank: 1 | 2 | 3): Promise<void> => {
    const selectedVirtual = offers.find(o => o.id === offerId);
    if (!selectedVirtual) return;

    const termMonths = selectedVirtual.termMonths;
    const parentOfferId = selectedVirtual.extn?.parentOfferId as string | undefined ?? offerId;
    const currentSiteId = selectedVirtual.siteId;
    const retailerName = selectedVirtual.retailerName;
    const rankLabel = rank === 1 ? '1st Choice' : rank === 2 ? '2nd Choice' : '3rd Choice';

    // Check if this offer+term is already assigned this rank.
    // ⚠️ Shared mode used to look for `!s.siteId` — "the entry with no site". Now that every
    // entry carries a siteId, shared mode owns ONE rank slot for the whole tender, so it
    // matches on rank alone. inSelectionScope is the single predicate shared with usedRanks
    // below; when the two disagreed, a rank could be counted as taken but be unreachable by
    // the deselect filter, which is what produced the unclearable "Maximum 3" state.
    const existingForRank = rankedSelections.find(
      s => s.rank === rank && inSelectionScope(s, currentSiteId)
    );
    const isCurrentlyThisRank = allSitesSameAbn
      ? existingForRank?.retailerName === retailerName && existingForRank?.termMonths === termMonths
      : existingForRank?.offerId === parentOfferId && existingForRank?.termMonths === termMonths;

    // Build updated selections: clear this rank slot (scoped identically to the lookup above),
    // then add the new entry — or nothing, when toggling the same pick off.
    const withoutThisRank = rankedSelections.filter(
      s => !(s.rank === rank && inSelectionScope(s, currentSiteId))
    );
    // Both siteId and retailerName are always written — see RankedOfferSelection's note on why
    // the old either/or shape made entries unreadable after a mode flip.
    const newSelections = isCurrentlyThisRank
      ? withoutThisRank  // deselect
      : [...withoutThisRank, { rank, offerId: parentOfferId, termMonths, siteId: currentSiteId, retailerName }];

    setRankedSelections(newSelections);

    // Rank 1: also update offer's is_recommended / recommended_months for backward compat.
    // Per-site mode: only clear the flag on OTHER offers belonging to the SAME site — offers
    // on other sites must keep their own recommended flag untouched.
    // Shared mode: every site has its OWN raw offer row for this same retailer (one row per
    // tender_site) — match by retailer name instead of exact row id, so rank 1 flags THIS
    // retailer's row on every site, not just the one whose card was clicked.
    if (rank === 1) {
      const updatedDbOffers = dbOffers.map(dbOffer => {
        const isMatch = allSitesSameAbn
          ? dbOffer.retailerName === retailerName
          : dbOffer.id === parentOfferId;
        if (!isMatch) {
          if (!allSitesSameAbn && dbOffer.siteId !== currentSiteId) return dbOffer;
          return (dbOffer.recommendedMonths?.length ?? 0) > 0
            ? { ...dbOffer, recommendedMonths: [], isRecommended: false }
            : dbOffer;
        }
        const newTerms = isCurrentlyThisRank ? [] : [termMonths];
        return { ...dbOffer, recommendedMonths: newTerms, isRecommended: newTerms.length > 0, updatedAt: new Date() };
      });

      setLocalOffers(updatedDbOffers);
      setOffers(updatedDbOffers);

      if (tenderId) {
        try {
          const { getUserOrganizationId, saveOffers, getOffers, saveTenderSites } = await import('../services/tenderApiService');
          const orgId = await getUserOrganizationId();
          if (orgId) {
            const siteIdMap = await buildSiteIdMap(orgId, saveTenderSites);
            if (siteIdMap.size > 0) {
              const stampedRankOffers = updatedDbOffers.map(offer => ({
                ...offer,
                extn: { ...(offer.extn || {}), selectedRateType },
              }));
              await saveOffers({ orgId, clientId: clientData.id || '', tenderId, offers: stampedRankOffers, siteIdMap });
              const reloadResult = await getOffers(tenderId);
              if (reloadResult.success && reloadResult.offers?.length > 0) {
                const mappedOffers = reloadResult.offers.map((dbOffer: any) => {
                  const matchingSite = sites.find(s => s.id === dbOffer.siteId);
                  return matchingSite
                    ? { ...dbOffer, siteId: matchingSite.id, siteName: matchingSite.name || dbOffer.siteName }
                    : dbOffer;
                });
                const hasValidOffers = mappedOffers.some((o: any) => o.siteId && sites.some(s => s.id === o.siteId));
                if (hasValidOffers) {
                  setLocalOffers(mappedOffers);
                  setOffers(mappedOffers);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error saving rank 1 offer:', error);
        }
      }
    }

    // Persist ranked selections to tender, and mirror the 1st Choice onto the offer rows.
    //
    // ⚠️ THE MIRROR BELONGS HERE, NOT IN THE RANK-1 BRANCH ABOVE. That one rewrites every offer
    // through saveOffers behind an org, a client and a site map, and exits silently when any is
    // missing — which is how a tender ended up with all three picks stored and every offer still
    // reading is_recommended: false. This writes two columns by id, from the same selections
    // being saved on the line above, on every rank change including a deselect.
    if (tenderId) {
      try {
        const { saveRankedOfferSelections, saveRecommendedFlags } = await import('../services/tenderApiService');
        await saveRankedOfferSelections(tenderId, newSelections);
        await saveRecommendedFlags(
          recommendedFlagsForOffers(dbOffers, newSelections, allSitesSameAbn),
        );
      } catch (error) {
        console.error('Error saving ranked selections:', error);
      }
    }

    toast({
      title: isCurrentlyThisRank ? `${rankLabel} Unselected` : `${rankLabel} Selected`,
      description: isCurrentlyThisRank
        ? `${selectedVirtual.retailerName} (${termMonths}m) removed from ${rankLabel}.`
        : `${selectedVirtual.retailerName} (${termMonths}m) set as ${rankLabel}.`,
    });
  };

  // Backward-compatible alias
  const handleSelectRecommended = (offerId: string) => handleSelectRank(offerId, 1);

  // Sequential multi-select: clicking assigns next available rank (1→2→3).
  // Clicking an already-selected offer deselects it.
  const handleSelectOffer = (offerId: string) => {
    const virtual = offers.find(o => o.id === offerId);
    if (!virtual) return;
    const parentOfferId = (virtual.extn?.parentOfferId as string | undefined) ?? offerId;
    const termMonths = virtual.termMonths ?? 0;

    // Shared mode: match by retailer+term (a different site's card for the same retailer
    // is the "same" selection); per-site mode: match by this exact offer row.
    const existing = allSitesSameAbn
      ? rankedSelections.find(s => s.retailerName === virtual.retailerName && s.termMonths === termMonths)
      : rankedSelections.find(s => s.offerId === parentOfferId && s.termMonths === termMonths);

    if (existing) {
      handleSelectRank(offerId, existing.rank);
    } else {
      // Per-site mode: scoped to this offer's own site — each site gets its own independent
      // 3 rank slots. Shared mode: one shared set of 3 rank slots for the whole tender.
      // Uses the SAME scope predicate as handleSelectRank's clear-this-rank filter, so a rank
      // counted as taken here is always one the user can actually free by deselecting.
      const usedRanks = new Set(
        rankedSelections.filter(s => inSelectionScope(s, virtual.siteId)).map(s => s.rank)
      );
      const nextRank = ([1, 2, 3] as const).find(r => !usedRanks.has(r));
      if (nextRank) {
        handleSelectRank(offerId, nextRank);
      } else {
        toast({
          title: allSitesSameAbn ? 'Maximum 3 offers selected' : 'Maximum 3 offers selected for this site',
          description: 'Deselect an offer to choose another.',
        });
      }
    }
  };

  // Handle Continue - Save all offers before proceeding
  const handleContinue = async () => {
    setIsContinueLoading(true);
    try {
      // Save all offers to ensure recommended flags are persisted
      if (tenderId && dbOffers.length > 0) {
        try {
          const { getUserOrganizationId, saveOffers, getOffers, saveTenderSites } = await import('../services/tenderApiService');
          const orgId = await getUserOrganizationId();
          if (!orgId) {
            console.error('No organization ID found');
            onNext(); // Proceed anyway
            return;
          }

          // Build siteIdMap — creates tender_site records if missing
          const siteIdMap = await buildSiteIdMap(orgId, saveTenderSites);

          if (siteIdMap.size > 0) {
            // Stamp selectedRateType onto each offer's extn so calculation engine uses it
            const offersWithRateType = dbOffers.map(offer => ({
              ...offer,
              extn: { ...(offer.extn || {}), selectedRateType },
            }));
            // Save PARENT DB offers (not virtual offers with fake IDs)
            await saveOffers({
              orgId,
              clientId: clientData.id || '',
              tenderId,
              offers: offersWithRateType,
              siteIdMap,
            });

            // Reload offers to ensure DB state is synced
            const reloadResult = await getOffers(tenderId);
            if (reloadResult.success && reloadResult.offers && reloadResult.offers.length > 0) {
              const mappedOffers = reloadResult.offers.map((dbOffer: any) => {
                const matchingSite = sites.find(s => s.id === dbOffer.siteId);
                if (matchingSite) {
                  return {
                    ...dbOffer,
                    siteId: matchingSite.id,
                    siteName: matchingSite.name || dbOffer.siteName,
                  };
                }
                return dbOffer;
              });
              console.log('✅ CompareAndSelectStep: Reloaded offers on continue:', mappedOffers.length);
              setLocalOffers(mappedOffers);
              setOffers(mappedOffers);
            } else {
              console.warn('⚠️ CompareAndSelectStep: Reload on continue failed/empty, keeping existing offers');
            }

            // TRIGGER CALCULATIONS: Compute costs for all offers so they show in the UI.
            // This is essential when users come via the email parsing flow (not OffersStep)
            // since OffersStep is the only other place that triggers calculations.
            console.log('🧮 CompareAndSelectStep: Triggering calculation for tender:', tenderId);
            try {
              const calcResult = await calculateTenderCosts(tenderId);
              reportSkippedTerms(calcResult.skippedTerms);
              if (calcResult.success) {
                console.log(`✅ Calculations complete: ${calcResult.calculated} offers calculated`);
                // Refresh calculated costs in UI
                hasFetchedCostsRef.current = false;
                hasTriedFallbackRef.current = false;
              } else {
                console.warn('⚠️ Calculation completed with errors:', calcResult.errors);
              }
            } catch (calcError) {
              console.error('❌ Failed to trigger calculations:', calcError);
              // Don't block navigation — calculations can run later in OfferDetailsStep
            }
          } else {
            console.error('❌ No tender_site records available — offers not saved');
          }
        } catch (error) {
          console.error('Error saving offers before continue:', error);
          // Proceed anyway - don't block user
        }
      }

      onNext();
    } finally {
      setIsContinueLoading(false);
    }
  };

  // Get retailer info
  const getRetailerInfo = (offer: Offer): Retailer | null => {
    return retailers.find(r => r.id === offer.retailerId) || null;
  };

  // Get rate data for display
  const getRateData = (offer: Offer, rateType: 'smooth' | 'stepped' = 'smooth') => {
    return offer.multiYearRateData?.[rateType] || null;
  };


  // Check if ready for next step.
  // Shared mode: one rank-1 selection covers the whole tender. Per-site mode: every site
  // that actually has offers must have its own rank-1 (recommended) selection, not just
  // any single site in the tender.
  const isReadyForNext = (): boolean => {
    if (filteredOffers.length === 0) return false;
    // The rule lives in hasRequiredRankOneSelections — this reader was the one left behind when
    // selections started carrying a siteId in shared mode, and it is now the only copy.
    return hasRequiredRankOneSelections({
      allSitesSameAbn,
      selections: rankedSelections,
      siteIdsWithOffers: sites
        .filter(site => offers.some(o => o.siteId === site.id))
        .map(site => site.id),
    });
  };

  // Toggle section expansion
  const toggleSection = (section: SectionKey): void => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Find best values for highlighting - now uses REAL calculated costs
  const getBestValues = () => {
    if (filteredOffers.length === 0) return {};

    const bestValues: Record<string, { value: number; offerId: string }> = {};

    // Best annual cost (lowest) - USE CALCULATED COSTS
    const annualCosts = filteredOffers
      .map(o => {
        // Get calculated annual cost, fallback to estimatedAnnualCost if no calculation yet
        const calculatedCost = getCalculatedAnnualCost(o.id, o.termMonths);
        const cost = calculatedCost ?? o.estimatedAnnualCost ?? Infinity;
        return { id: o.id, cost };
      })
      .filter(o => o.cost !== Infinity);
    if (annualCosts.length > 0) {
      const best = annualCosts.reduce((min, curr) => curr.cost < min.cost ? curr : min);
      bestValues.lowestCost = { value: best.cost, offerId: best.id };
    }

    // Best savings (highest) - compute from calculated costs relative to highest cost offer
    const offersWithCosts = filteredOffers
      .map(o => {
        const calculatedCost = getCalculatedAnnualCost(o.id, o.termMonths);
        return { id: o.id, cost: calculatedCost ?? o.estimatedAnnualCost ?? Infinity };
      })
      .filter(o => o.cost !== Infinity && o.cost > 0);

    if (offersWithCosts.length > 1) {
      const highestCost = Math.max(...offersWithCosts.map(o => o.cost));
      const savings = offersWithCosts.map(o => ({
        id: o.id,
        percent: highestCost > 0 ? ((highestCost - o.cost) / highestCost) * 100 : 0,
      }));
      const best = savings.reduce((max, curr) => curr.percent > max.percent ? curr : max);
      if (best.percent > 0) {
        bestValues.highestSavings = { value: best.percent, offerId: best.id };
      }
    }

    // Best green percentage (highest)
    const greenPercents = filteredOffers.map(o => ({ id: o.id, percent: o.greenPercent || 0 }));
    if (greenPercents.length > 0) {
      const best = greenPercents.reduce((max, curr) => curr.percent > max.percent ? curr : max);
      bestValues.highestGreen = { value: best.percent, offerId: best.id };
    }

    // If user has manually selected a recommended offer, it wins the cost + savings badges
    const recommendedOffer = filteredOffers.find(o => o.recommendedMonths && o.recommendedMonths.length > 0);
    if (recommendedOffer) {
      bestValues.lowestCost = { value: bestValues.lowestCost?.value ?? 0, offerId: recommendedOffer.id };
      bestValues.highestSavings = { value: bestValues.highestSavings?.value ?? 0, offerId: recommendedOffer.id };
    }

    return bestValues;
  };

  // Returns the user-selected rank (1/2/3) for a specific offer+term combination.
  // Per-site mode: only returns a rank if the user explicitly selected THIS exact card —
  // not all cards of the same retailer (each site's card is independent).
  // Shared mode: matches by retailer+term instead, so the SAME retailer's card shows the
  // same rank badge on every site — that's the whole point of sharing the selection.
  // Reads from rankedSelections, not calculated cost rank.
  const getDisplayRank = (offerId: string, termMonths: number): number | null => {
    const virtual = offers.find(o => o.id === offerId);
    const parentOfferId = (virtual?.extn?.parentOfferId as string | undefined) ?? offerId;
    const selection = allSitesSameAbn
      ? rankedSelections.find(s => s.retailerName === virtual?.retailerName && s.termMonths === termMonths)
      : rankedSelections.find(s => s.offerId === parentOfferId && s.termMonths === termMonths);
    return selection?.rank ?? null;
  };

  // DEBUG: Log offer <-> cost ID matching when both are available
  useEffect(() => {
    if (offers.length > 0 && calculatedCosts.size > 0) {
      console.log('🔍 DEBUG - Offer ID <-> Cost ID Matching:');
      offers.forEach(offer => {
        const realId = resolveOfferId(offer.id);
        const hasCost = calculatedCosts.has(realId);
        const costData = calculatedCosts.get(realId);
        console.log(`   offer.id="${offer.id}" -> realId="${realId}" (${offer.retailerName}), termMonths=${offer.termMonths}, hasCost=${hasCost}, perYear=${costData?.[offer.termMonths.toString()]?.perYear || 'N/A'}`);
      });
      console.log(`   Total offers: ${offers.length}, Total costs: ${calculatedCosts.size}`);
    }
  }, [offers, calculatedCosts]);

  const bestValues = getBestValues();

  // Check if value is best
  const isBestValue = (offerId: string, type: 'cost' | 'savings' | 'green'): boolean => {
    if (type === 'cost' && bestValues.lowestCost) {
      return bestValues.lowestCost.offerId === offerId;
    }
    if (type === 'savings' && bestValues.highestSavings) {
      return bestValues.highestSavings.offerId === offerId;
    }
    if (type === 'green' && bestValues.highestGreen) {
      return bestValues.highestGreen.offerId === offerId;
    }
    return false;
  };

  if (sites.length === 0) {
    return (
      <div className="w-full space-y-6">
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MapPin className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Sites Available</h3>
            <p className="text-sm text-gray-600 text-center max-w-md">
              Please add sites in the previous step before comparing offers.
            </p>
          </CardContent>
        </Card>
        <WorkflowNavigation
          onNext={onNext}
          onBack={onPrevious}
          isNextDisabled={true}
          nextButtonText="Continue"
          backButtonText="Back to Offers"
        />
      </div>
    );
  }

  // Show loading spinner while offers are being fetched from DB
  if (isLoadingOffers) {
    return (
      <div className="w-full space-y-6">
        <Card className="border-2 border-primary/10">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Offers</h3>
            <p className="text-sm text-gray-500 text-center max-w-md">
              Fetching energy offers for this tender, please wait...
            </p>
          </CardContent>
        </Card>
        <WorkflowNavigation
          onNext={onNext}
          onBack={onPrevious}
          isNextDisabled={true}
          nextButtonText="Continue to Template"
          backButtonText="Back to Offers"
        />
      </div>
    );
  }

  if (filteredOffers.length === 0) {
    return (
      <div className="w-full space-y-6">
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Offers Available</h3>
            <p className="text-sm text-gray-600 text-center max-w-md">
              {selectedSite ? `No offers found for ${selectedSite.name}. Please add offers in the previous step.` : 'Please select a site to view offers.'}
            </p>
          </CardContent>
        </Card>
        <WorkflowNavigation
          onNext={onNext}
          onBack={onPrevious}
          isNextDisabled={true}
          nextButtonText="Continue"
          backButtonText="Back to Offers"
        />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Compare & Select Offers</h2>
          <p className="text-muted-foreground mt-1">
            Compare all offers for {clientData.name} and select the recommended offer with contract term
          </p>
        </div>
        {/* View Mode Toggle Buttons - Always visible */}
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant={viewMode === 'cards' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              console.log('Switching to card view');
              setViewMode('cards');
            }}
            className="flex items-center gap-2"
            type="button"
          >
            <LayoutGrid className="w-4 h-4" />
            Cards
          </Button>
          {/* <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              console.log('Switching to table view');
              setViewMode('table');
            }}
            className="flex items-center gap-2"
            type="button"
          >
            <Table2 className="w-4 h-4" />
            Table
          </Button> */}
        </div>
      </div>

      {/* Site Selection */}
      {siteGroups.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-gray-700">Select Group:</span>
              </div>
              <Select
                value={selectedGroup?.key ?? ''}
                onValueChange={(key) => {
                  const group = siteGroups.find(g => g.key === key);
                  if (group?.sites[0]) setSelectedSiteId(group.sites[0].id);
                }}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Choose a group">
                    {selectedGroup && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{groupLabel(selectedGroup, groupNames)}</span>
                        {selectedGroup.nmis.length > 0 && (
                          <span className="text-xs text-gray-500">({selectedGroup.nmis.join(', ')})</span>
                        )}
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {siteGroups.map((group) => (
                    <SelectItem key={group.key} value={group.key}>
                      <div className="flex flex-col">
                        <span className="font-medium">{groupLabel(group, groupNames)}</span>
                        {group.nmis.length > 0 && (
                          <span className="text-xs text-gray-500">NMI: {group.nmis.join(', ')}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* COMMON Contract Term Filter Dropdown */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-200 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Filter by Contract Term</h3>
                <p className="text-sm text-gray-600 mt-1">Select a contract term to filter all offers</p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 max-w-sm">
                <Select
                  value={selectedContractYear?.toString() || 'all'}
                  onValueChange={(value) => {
                    setSelectedContractYear(value === 'all' ? null : parseInt(value));
                  }}
                >
                  <SelectTrigger className="w-full h-12 bg-white border-2 border-blue-400 hover:border-blue-500 transition-colors shadow-md text-base font-medium">
                    <SelectValue placeholder="All Contract Terms">
                      {selectedContractYear ? `Year ${selectedContractYear}` : 'All Contract Terms'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Contract Terms</SelectItem>
                    {contractYearOptions.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {`Year ${year}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedContractYear && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-200 rounded-md border border-blue-300">
                  <CheckCircle2 className="w-5 h-5 text-blue-700" />
                  <span className="text-base font-semibold text-blue-800">
                    Showing {shownTermLabel} offers only
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading banner — shown while costs are being fetched / calculated */}
      {isLoadingCosts && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Calculating costs…</p>
            <p className="text-xs text-blue-500 mt-0.5">Fetching annual costs for all offers — this usually takes a few seconds.</p>
          </div>
        </div>
      )}

      {/* Comparison View - Default is card view */}
      {viewMode === 'cards' ? (
        <ComparisonCardsView
          offers={filteredOffers}
          retailers={retailers}
          getRetailerInfo={getRetailerInfo}
          getRateData={getRateData}
          formatCurrency={formatCurrency}
          formatRate={formatRate}
          formatPercent={formatPercent}
          handleSelectOffer={handleSelectOffer}
          isBestValue={isBestValue}
          getDisplayRank={getDisplayRank}
          selectedSite={selectedSite}
          getCalculatedAnnualCost={getCalculatedAnnualCost}
          getCalculatedBreakdown={getCalculatedBreakdown}
          isLoadingCosts={isLoadingCosts}
          selectedRateType={selectedRateType}
        />
      ) : (
        <ComparisonTableView
          offers={filteredOffers}
          retailers={retailers}
          getRetailerInfo={getRetailerInfo}
          getRateData={getRateData}
          formatCurrency={formatCurrency}
          formatRate={formatRate}
          formatPercent={formatPercent}
          handleSelectRecommended={handleSelectRecommended}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          isBestValue={isBestValue}
          selectedSite={selectedSite}
          getCalculatedAnnualCost={getCalculatedAnnualCost}
          getCalculatedBreakdown={getCalculatedBreakdown}
          isLoadingCosts={isLoadingCosts}
          rankedSelections={rankedSelections}
          getDisplayRank={getDisplayRank}
          selectedRateType={selectedRateType}
          handleSelectRank={handleSelectRank}
        />
      )}

      {/* Navigation */}
      <WorkflowNavigation
        onNext={handleContinue}
        onBack={onPrevious}
        isNextDisabled={!isReadyForNext()}
        isNextLoading={isContinueLoading}
        nextButtonText="Continue to Template"
        backButtonText="Back to Offers"
      />
    </div>
  );
};

// ─── Helpers shared by both views ─────────────────────────────────────────────

/** Resolve the best contract-term key from extn.contract_terms for a virtual offer. */
export function resolveContractTermKey(offer: Offer, rateType: 'Smooth' | 'Stepped' = 'Smooth'): string | null {
  const ct = offer.extn?.contract_terms as Record<string, any> | undefined;
  if (!ct) return null;
  const t = offer.termMonths.toString();
  const wantSuffix  = rateType === 'Smooth' ? `${t}_smooth`  : `${t}_stepped`;
  const otherSuffix = rateType === 'Smooth' ? `${t}_stepped` : `${t}_smooth`;
  const otherType   = rateType === 'Smooth' ? 'Stepped' : 'Smooth';

  // 1. Correctly-suffixed key for the wanted rate type
  if (ct[wantSuffix]) return wantSuffix;

  // 2. Plain (unsuffixed) key — some retailers (e.g. NBE, Origin when both types are offered)
  //    store one rate type under a bare key like "12" with no suffix at all, while the other
  //    type lives at "12_stepped"/"12_smooth". Only treat the plain key as a match here if it
  //    doesn't explicitly say it's the OTHER type — otherwise this falls through to step 3,
  //    which previously short-circuited here and silently returned the wrong rate type.
  if (ct[t] && ct[t].rate_type !== otherType) return t;

  // 3. Nothing found for the wanted type — fall back to whatever exists (last resort only)
  if (ct[otherSuffix]) return otherSuffix;
  if (ct[t]) return t;
  return null;
}

/** Return sorted year keys (year_1, year_2 …) for the resolved term. */
function getTermYearKeys(offer: Offer, rateType: 'Smooth' | 'Stepped' = 'Smooth'): string[] {
  const key = resolveContractTermKey(offer, rateType);
  if (!key) return [];
  const termData = (offer.extn?.contract_terms as Record<string, any>)[key];
  return Object.keys(termData)
    .filter(k => /^year_\d+$/.test(k))
    .sort((a, b) => parseInt(a.replace('year_', ''), 10) - parseInt(b.replace('year_', ''), 10));
}

/** Get a single rate value (peak/offpeak/shoulder) for a year from extn.contract_terms.
 *  Returns 0 if not found. Rates are in c/kWh.
 */
function getContractTermRate(offer: Offer, yearKey: string, rateField: 'peak' | 'offpeak' | 'shoulder', rateType: 'Smooth' | 'Stepped' = 'Smooth'): number {
  const key = resolveContractTermKey(offer, rateType);
  if (!key) return 0;
  const yearData = (offer.extn?.contract_terms as Record<string, any>)[key]?.[yearKey];
  return yearData?.[rateField] ?? 0;
}

/** Format a rate that is already in c/kWh (no × 100 conversion needed). */
function formatCentsKwh(rate: number): string {
  // Full quoted precision, never 2 dp — see lib/ratePrecision. AGL quotes 9.9076 c/kWh and
  // rounding it to 9.91 both misreports the rate and, where a cost column is derived from the
  // displayed figure, moves the money (62,740 kWh x 9.91 = $6,217.53 against a true $6,215.99).
  return `${formatEnergyRate(rate)} c/kWh`;
}

/** Derive the actual rate type being shown for an offer given user's preference.
 *  Returns { actual, isFallback } — actual is null when offer has no keyed terms. */
export function getOfferRateTypeInfo(offer: Offer, selectedRateType: 'Smooth' | 'Stepped'): { actual: 'Smooth' | 'Stepped' | null; isFallback: boolean } {
  const resolvedKey = resolveContractTermKey(offer, selectedRateType);
  if (!resolvedKey) return { actual: null, isFallback: false };

  // Prefer the explicit rate_type field on the resolved term data — some retailers
  // (e.g. EnergyAustralia) store Smooth under a PLAIN key like "12" with no "_smooth"
  // suffix at all, so a key-string check alone leaves the badge blank for them.
  // Fall back to the key suffix only when rate_type is missing/unrecognised.
  const ct = offer.extn?.contract_terms as Record<string, any> | undefined;
  const termRateType = ct?.[resolvedKey]?.rate_type;
  const actual: 'Smooth' | 'Stepped' | null =
    termRateType === 'Smooth' || termRateType === 'Stepped'
      ? termRateType
      : resolvedKey.includes('_stepped')
        ? 'Stepped'
        : resolvedKey.includes('_smooth')
          ? 'Smooth'
          : null;

  if (!actual) return { actual: null, isFallback: false };
  return { actual, isFallback: selectedRateType !== actual };
}

/**
 * Returns true if an env charge object has at least one non-null rate value.
 * Handles two storage formats:
 *   Flat:   { year_1: { lrec_rate: 0.0775, sres_rate: null, veec_rate: null } }
 *   Nested: { LREC: { year_1: { ratePerKwh: 0.0775 } }, SREC: {...}, ... }
 */
const ENV_RATE_KEY_RE = /_rate$/i;

function hasAnyRealEnvValue(env: unknown): boolean {
  if (!env || typeof env !== 'object') return false;
  for (const topVal of Object.values(env as Record<string, unknown>)) {
    if (!topVal || typeof topVal !== 'object') continue;
    const top = topVal as Record<string, unknown>;
    // Flat format: top keys are year_N, values have any `${scheme}_rate` field
    // (lrec_rate/sres_rate/veec_rate/esc_rate/reps_rate/eeis_rate/... — any registered or future scheme)
    if (Object.entries(top).some(([k, v]) => ENV_RATE_KEY_RE.test(k) && v != null) ||
        top.ratePerKwh != null || top.rate_per_kwh != null) return true;
    // Nested format: top keys are LREC/SREC/VEEC/ESC/REPS/EEIS/..., values are { year_1: { ratePerKwh } }
    for (const yearVal of Object.values(top)) {
      if (!yearVal || typeof yearVal !== 'object') continue;
      const yr = yearVal as Record<string, unknown>;
      if (yr.ratePerKwh != null || yr.rate_per_kwh != null ||
          Object.entries(yr).some(([k, v]) => ENV_RATE_KEY_RE.test(k) && v != null)) return true;
    }
  }
  return false;
}

/**
 * Get env charges for an offer, merged with zero-defaults so null rates display as $0.0000
 * rather than — (matching how OfferManagementCard/DualRateTable behaves in OffersStep).
 * Priority: multiYearRateData.smooth.environmentalCharges → extn.environmentalCharges →
 * extn.environmental_charges → empty defaults.
 *
 * multiYearRateData is the ONLY copy field edits (updateOfferRateData) actually write to — the
 * extn copies are a legacy mirror that saveOffers only ever populated once and never updates
 * again, so they go stale the moment a user edits/deletes a rate. Previously extn was checked
 * FIRST (skipped only if literally all-null), which meant a deliberately-zeroed rate in
 * multiYearRateData lost to a stale non-zero extn value — the edit would look like it never
 * took effect. multiYearRateData now wins unconditionally whenever it exists at all, even if its
 * values happen to be all zero (that's a real, deliberate state, not "missing data").
 */
/**
 * The offer's environmental charges exactly as stored — no defaults merged in.
 *
 * getOfferEnvCharges deep-merges against createEmptyEnvironmentalCharges(), which supplies
 * year_1 … year_6 for every scheme, so it can never answer "which years does this offer
 * actually price". Anything sizing year rows must come here instead.
 */
function getOfferRawEnvCharges(offer: Offer): unknown {
  const extnEnv = (offer.extn as any)?.environmentalCharges;
  const extnEnvSnake = (offer.extn as any)?.environmental_charges;
  const smoothEnv = (offer.multiYearRateData as any)?.smooth?.environmentalCharges;
  return (
    smoothEnv ||
    (hasAnyRealEnvValue(extnEnv) ? extnEnv : null) ||
    (hasAnyRealEnvValue(extnEnvSnake) ? extnEnvSnake : null)
  );
}

/**
 * How many year rows an offer should show: its term's year count, but never more years than it
 * actually prices.
 *
 * ceil(termMonths / 12) alone put an empty "Year 5" row on this screen after a reviewer deleted
 * Year 5 in the parsing step — the deletion persisted, but the row count came from the term and
 * the values came from a defaults-merged shape, so the slot rendered as zeros. Both the energy
 * rate maps and the environmental schemes are consulted, so a year priced by either still shows.
 */
function offerYearRowCount(offer: Offer): number {
  const nominal = Math.max(1, Math.ceil(offer.termMonths / 12));
  const smooth = (offer.multiYearRateData as any)?.smooth;
  const priced = Math.max(
    maxPricedYear(getOfferRawEnvCharges(offer)),
    maxPricedYear(smooth?.peakRates),
    maxPricedYear(smooth?.offpeakRates),
    maxPricedYear(smooth?.shoulderRates),
  );
  return priced > 0 ? Math.min(nominal, priced) : nominal;
}

function getOfferEnvCharges(offer: Offer): EnvironmentalCharges {
  const extnEnv = (offer.extn as any)?.environmentalCharges;
  const extnEnvSnake = (offer.extn as any)?.environmental_charges;
  const smoothEnv = (offer.multiYearRateData as any)?.smooth?.environmentalCharges;
  const raw =
    smoothEnv ||
    (hasAnyRealEnvValue(extnEnv) ? extnEnv : null) ||
    (hasAnyRealEnvValue(extnEnvSnake) ? extnEnvSnake : null);
  // No defaults passed to normalizeEnvironmentalCharges here — this offer's rates were already
  // resolved once, server-side, by offerAutoPopulateService (real parsed rate, formula-derived,
  // or explicitly left blank in review). Re-deriving here on every render would silently
  // overwrite a deliberate "leave this blank" decision with a freshly computed number.
  const normalized = normalizeEnvironmentalCharges(raw as Record<string, unknown> | undefined);
  const defaults = createEmptyEnvironmentalCharges();
  if (!normalized) return defaults;
  // Deep-merge: prefer normalized values but fall back to 0 (not null) for missing fields
  const mergeYear = (def: EnvironmentalChargeYearData, src: EnvironmentalChargeYearData | undefined): EnvironmentalChargeYearData =>
    src ? {
      ratePerKwh: src.ratePerKwh != null ? src.ratePerKwh : def.ratePerKwh,
      powerPercentage: src.powerPercentage != null ? src.powerPercentage : def.powerPercentage,
      pricePerCertificate: src.pricePerCertificate != null ? src.pricePerCertificate : def.pricePerCertificate,
    } : def;
  const mergeYearMap = (
    defMap: Record<string, EnvironmentalChargeYearData>,
    srcMap: Record<string, EnvironmentalChargeYearData> | undefined
  ): Record<string, EnvironmentalChargeYearData> => {
    if (!srcMap) return defMap;
    const result = { ...defMap };
    Object.keys(defMap).forEach(yearKey => {
      result[yearKey] = mergeYear(defMap[yearKey], srcMap[yearKey]);
    });
    return result;
  };

  const result: EnvironmentalCharges = {
    LREC: mergeYearMap(defaults.LREC, normalized.LREC as Record<string, EnvironmentalChargeYearData>) as EnvironmentalCharges['LREC'],
    SREC: mergeYearMap(defaults.SREC, normalized.SREC as Record<string, EnvironmentalChargeYearData>) as EnvironmentalCharges['SREC'],
    "VEEC/ESS/REES": mergeYearMap(defaults["VEEC/ESS/REES"], normalized["VEEC/ESS/REES"] as Record<string, EnvironmentalChargeYearData>) as EnvironmentalCharges['VEEC/ESS/REES'],
  };
  // Include every OTHER scheme present (ESC/PRC/REPS/EEIS/...) — not just the 3 legacy ones.
  // These don't have a zero-filled default map, so pass normalized data straight through;
  // consumers already treat null ratePerKwh as "no data".
  Object.keys(normalized).forEach(key => {
    if (key === 'LREC' || key === 'SREC' || key === 'VEEC/ESS/REES') return;
    result[key] = normalized[key] as Record<string, EnvironmentalChargeYearData>;
  });
  return result;
}

/** Schemes beyond the always-shown LREC/SREC/VEEC — rendered as extra rows only when an offer actually has data for them */
const ADDITIONAL_ENV_SCHEMES = ENV_SCHEMES.filter(s => !['lrec', 'sres', 'veec'].includes(s.key));

/** True if at least one offer has a real (non-null) rate/cert-price for the given scheme display key */
function offersHaveSchemeData(offers: Offer[], displayKey: string): boolean {
  return offers.some(offer => {
    const schemeData = getOfferEnvCharges(offer)?.[displayKey];
    if (!schemeData) return false;
    return Object.values(schemeData).some(yr => yr?.ratePerKwh != null || yr?.pricePerCertificate != null);
  });
}

// ──────────────────────────────────────────────────────────────────────────────

/**
 * Aggregate site-level costs from breakdown.sites[].
 * The top-level breakdown.perYear/energy/etc. are buggy (wrong scale).
 * The sites[] array has correct per-site annual costs.
 */
type SiteAggCosts = {
  annualOfferCost: number;   // sum sites[*].total  (annual)
  annualCurrentCost: number; // sum sites[*].current_total  (annual)
  annualSavings: number;     // annualCurrentCost - annualOfferCost
  savingsPercent: number;    // annualSavings / annualCurrentCost * 100
  energy: number;            // sum sites[*].energy  (annual)
  environment: number;       // sum sites[*].environment  (annual)
  network: number;           // sum sites[*].network  (annual)
  others: number;            // sum sites[*].others  (annual)
};

function getSiteAggregatedCosts(breakdown: ContractCostBreakdown): SiteAggCosts | null {
  const sites = breakdown.sites;
  if (!sites || sites.length === 0) return null;

  const annualOfferCost   = sites.reduce((s, x) => s + (x.total           || 0), 0);
  const annualCurrentCost = sites.reduce((s, x) => s + (x.current_total   || 0), 0);
  const annualSavings     = annualCurrentCost - annualOfferCost;
  const savingsPercent    = annualCurrentCost > 0 ? (annualSavings / annualCurrentCost) * 100 : 0;
  const energy            = sites.reduce((s, x) => s + (x.energy          || 0), 0);
  const environment       = sites.reduce((s, x) => s + (x.environment     || 0), 0);
  const network           = sites.reduce((s, x) => s + (x.network         || 0), 0);
  const others            = sites.reduce((s, x) => s + (x.others          || 0), 0);

  return { annualOfferCost, annualCurrentCost, annualSavings, savingsPercent, energy, environment, network, others };
}

// ──────────────────────────────────────────────────────────────────────────────

// Table View Component
interface ComparisonTableViewProps {
  offers: Offer[];
  retailers: Retailer[];
  getRetailerInfo: (offer: Offer) => Retailer | null;
  getRateData: (offer: Offer, rateType?: 'smooth' | 'stepped') => any;
  formatCurrency: (amount: number) => string;
  formatRate: (rate: number) => string;
  formatPercent: (percent: number) => string;
  handleSelectRecommended: (offerId: string) => void;
  expandedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  isBestValue: (offerId: string, type: 'cost' | 'savings' | 'green') => boolean;
  selectedSite: any;
  getCalculatedAnnualCost: (offerId: string, termMonths?: number) => number | null;
  getCalculatedBreakdown: (offerId: string, termMonths: number) => ContractCostBreakdown | null;
  isLoadingCosts: boolean;
  rankedSelections: import('../types/offerTypes').RankedOfferSelection[];
  getDisplayRank: (offerId: string, termMonths: number) => number | null;
  // These three were referenced in the body below but never declared or passed — they are
  // locals of CompareAndSelectStep, so this component threw a ReferenceError the moment it
  // rendered. Threaded as props to match ComparisonCardsView, which always did this correctly.
  selectedRateType: 'Smooth' | 'Stepped';
  handleSelectRank: (offerId: string, rank: 1 | 2 | 3) => void | Promise<void>;
}

const ComparisonTableView: React.FC<ComparisonTableViewProps> = ({
  offers,
  retailers,
  getRetailerInfo,
  getRateData,
  formatCurrency,
  formatRate,
  formatPercent,
  handleSelectRecommended,
  expandedSections,
  toggleSection,
  isBestValue,
  selectedSite,
  getCalculatedAnnualCost,
  getCalculatedBreakdown,
  isLoadingCosts,
  rankedSelections,
  getDisplayRank,
  selectedRateType,
  handleSelectRank,
}) => {
  // Determine how many year rows to render based on the maximum contract term across all offers
  // Rows are sized by what the offers actually price, not by the longest term — otherwise a
  // year a reviewer deleted still gets a row, filled with zeros by the defaults merge.
  const yearsCount = Math.max(1, ...offers.map(offerYearRowCount));
  const yearsToRender = Array.from({ length: yearsCount }, (_, i) => `year_${i + 1}`);

  return (
    <Card className="border-2 border-primary/10 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold">Offer Comparison</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedSite?.name || 'Site'} • {offers.length} offer{offers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200 sticky top-[73px] z-20">
                <th className="text-left py-4 px-6 font-semibold text-gray-900 min-w-[200px] sticky left-0 bg-gray-50 z-20 shadow-sm">
                  Feature
                </th>
                {offers.map((offer) => {
                  const retailer = getRetailerInfo(offer);
                  const parentOfferId = (offer.extn as any)?.parentOfferId ?? offer.id;
                  const offerRank = rankedSelections.find(
                    s => s.offerId === parentOfferId && s.termMonths === offer.termMonths
                  )?.rank ?? null;
                  const isRank1 = offerRank === 1;
                  const isRank2 = offerRank === 2;
                  const isRank3 = offerRank === 3;

                  const rankBgClass =
                    isRank1 ? 'bg-green-50 border-l-4 border-l-green-500' :
                    isRank2 ? 'bg-blue-50 border-l-4 border-l-blue-400' :
                    isRank3 ? 'bg-purple-50 border-l-4 border-l-purple-400' :
                    'bg-white';

                  return (
                    <th
                      key={offer.id}
                      className={`text-center py-4 px-6 min-w-[220px] relative ${rankBgClass}`}
                    >
                      <div className="space-y-3">
                        {/* Retailer Logo & Name */}
                        <div className="flex flex-col items-center gap-2">
                          {retailer?.logo || offer.retailerLogo ? (
                            <div className="w-12 h-12 rounded-lg bg-white border-2 border-gray-200 flex items-center justify-center p-2">
                              <img
                                src={retailer?.logo || offer.retailerLogo}
                                alt={retailer?.name || offer.retailerName}
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-primary/10 border-2 border-primary/20 flex items-center justify-center">
                              <span className="text-lg font-bold text-primary">
                                {(retailer?.name || offer.retailerName || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="font-semibold text-gray-900 text-sm">
                            {retailer?.name || offer.retailerName}
                          </div>
                          {offerRank === 1 && (
                            <Badge className="bg-green-600 text-white text-xs px-2 py-1">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              1st Choice
                            </Badge>
                          )}
                          {offerRank === 2 && (
                            <Badge className="bg-blue-500 text-white text-xs px-2 py-1">
                              2nd Choice
                            </Badge>
                          )}
                          {offerRank === 3 && (
                            <Badge className="bg-purple-500 text-white text-xs px-2 py-1">
                              3rd Choice
                            </Badge>
                          )}
                          {(() => {
                            const { actual, isFallback } = getOfferRateTypeInfo(offer, selectedRateType);
                            if (!actual) return null;
                            return (
                              <span
                                title={isFallback ? `${selectedRateType} not available — showing ${actual}` : `${actual} rates`}
                                className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                                  actual === 'Smooth'
                                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                                    : 'bg-amber-50 text-amber-700 border-amber-300'
                                }`}
                              >
                                {actual}{isFallback && <span className="opacity-60 ml-0.5">*</span>}
                              </span>
                            );
                          })()}
                        </div>

                        {/* Contract Term - Static Display */}
                        <div className="space-y-1">
                          <div className="text-xs text-gray-600 font-medium">Contract Term:</div>
                          <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                            <Calendar className="w-3.5 h-3.5 text-gray-500" />
                            <span className="font-medium text-gray-900 text-sm">
                              {formatTermLabel(offer.termMonths)}
                            </span>
                          </div>
                        </div>

                        {/* Rank Selection Buttons */}
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant={isRank1 ? "default" : "outline"}
                            onClick={() => handleSelectRank(offer.id, 1)}
                            className={`w-full text-xs ${isRank1 ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'}`}
                          >
                            {isRank1 ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Star className="w-3 h-3 mr-1" />}
                            1st Choice
                          </Button>
                          <Button
                            size="sm"
                            variant={isRank2 ? "default" : "outline"}
                            onClick={() => handleSelectRank(offer.id, 2)}
                            className={`w-full text-xs ${isRank2 ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
                          >
                            {isRank2 ? <CheckCircle2 className="w-3 h-3 mr-1" /> : null}
                            2nd Choice
                          </Button>
                          <Button
                            size="sm"
                            variant={isRank3 ? "default" : "outline"}
                            onClick={() => handleSelectRank(offer.id, 3)}
                            className={`w-full text-xs ${isRank3 ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'}`}
                          >
                            {isRank3 ? <CheckCircle2 className="w-3 h-3 mr-1" /> : null}
                            3rd Choice
                          </Button>
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {/* Summary Section - Always Expanded - USING REAL CALCULATED COSTS */}
              <tr className="border-b-2 border-gray-200 bg-blue-50/50 hover:bg-blue-50">
                <td className="py-4 px-6 font-semibold text-gray-900 sticky left-0 bg-blue-50/50 z-10 shadow-sm">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    Annual Cost
                    {isLoadingCosts && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const isBest = isBestValue(offer.id, 'cost');

                  // USE REAL CALCULATED COST from sites - fallback to estimated if no calc yet
                  const calculatedCost = getCalculatedAnnualCost(offer.id, offer.termMonths);
                  const displayCost = calculatedCost ?? offer.estimatedAnnualCost;
                  const breakdown = getCalculatedBreakdown(offer.id, offer.termMonths);
                  const sc = breakdown ? getSiteAggregatedCosts(breakdown) : null;

                  return (
                    <td
                      key={offer.id}
                      className={`py-4 px-6 text-center relative ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-2">
                          {isLoadingCosts ? (
                            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                          ) : (
                            <div className={`text-2xl font-bold ${isBest ? 'text-green-600' : 'text-gray-900'}`}>
                              {displayCost ? formatCurrency(displayCost) : '—'}
                            </div>
                          )}
                          {isBest && !isLoadingCosts && (
                            <Award className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        {sc && sc.annualCurrentCost > 0 && (
                          <div className="text-xs text-gray-500">
                            Current: {formatCurrency(sc.annualCurrentCost)}/yr
                          </div>
                        )}
                        {sc && sc.annualSavings !== 0 && (
                          <div className={`text-sm flex items-center justify-center gap-1 ${sc.annualSavings > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {sc.annualSavings > 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                            {sc.annualSavings > 0 ? 'Save ' : 'Cost +'}{formatCurrency(Math.abs(sc.annualSavings))}/yr
                            {' '}({Math.abs(sc.savingsPercent).toFixed(1)}%)
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>

              {/* Total Contract Value */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Total Contract Value
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const breakdown = getCalculatedBreakdown(offer.id, offer.termMonths);

                  return (
                    <td
                      key={offer.id}
                      className={`py-3 px-6 text-center ${isRecommended ? 'bg-green-50/50' : ''}`}
                    >
                      {(() => {
                        const sc = breakdown ? getSiteAggregatedCosts(breakdown) : null;
                        const total = sc ? sc.annualOfferCost * contractWeightOf(breakdown, offer.termMonths) : null;
                        return (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {total != null ? formatCurrency(total) : '—'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ({formatTermLabel(offer.termMonths)})
                            </div>
                          </>
                        );
                      })()}
                    </td>
                  );
                })}
              </tr>

              {/* Rank */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-600" />
                    Rank
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const displayRank = getDisplayRank(offer.id, offer.termMonths);
                  return (
                    <td key={offer.id} className={`py-3 px-6 text-center ${isRecommended ? 'bg-green-50/50' : ''}`}>
                      {displayRank != null ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          displayRank === 1 ? 'bg-green-100 text-green-700' :
                          displayRank === 2 ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          #{displayRank}{displayRank === 1 ? ' · Best' : ''}
                        </span>
                      ) : <span className="text-sm text-gray-400">—</span>}
                    </td>
                  );
                })}
              </tr>

              {/* Savings vs Current Contract */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-green-600" />
                    Savings vs Current
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const bd = getCalculatedBreakdown(offer.id, offer.termMonths);
                  const sc = bd ? getSiteAggregatedCosts(bd) : null;
                  return (
                    <td key={offer.id} className={`py-3 px-6 text-center ${isRecommended ? 'bg-green-50/50' : ''}`}>
                      {sc && sc.annualSavings !== 0 ? (
                        <div className="space-y-0.5">
                          <div className={`font-semibold text-sm ${sc.annualSavings > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {sc.annualSavings > 0 ? '↓ ' : '↑ '}{formatCurrency(Math.abs(sc.annualSavings))}/yr
                          </div>
                          <div className={`text-xs ${sc.annualSavings > 0 ? 'text-green-500' : 'text-red-400'}`}>
                            {sc.annualSavings > 0 ? '-' : '+'}{Math.abs(sc.savingsPercent).toFixed(1)}% vs current
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatCurrency(Math.abs(sc.annualSavings) * contractWeightOf(bd, offer.termMonths))} over {formatTermLabel(offer.termMonths)}
                          </div>
                        </div>
                      ) : <div className="text-sm text-gray-400">—</div>}
                    </td>
                  );
                })}
              </tr>

              {/* Current Contract Annual Cost */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">Current Contract/yr</span>
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const bd = getCalculatedBreakdown(offer.id, offer.termMonths);
                  const sc = bd ? getSiteAggregatedCosts(bd) : null;
                  return (
                    <td key={offer.id} className={`py-3 px-6 text-center ${isRecommended ? 'bg-green-50/50' : ''}`}>
                      <div className="text-sm text-gray-500">
                        {sc && sc.annualCurrentCost > 0 ? formatCurrency(sc.annualCurrentCost) : '—'}
                      </div>
                    </td>
                  );
                })}
              </tr>

              {/* Cost Breakdown by Category (contract-period values) */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    Cost Breakdown
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const bd = getCalculatedBreakdown(offer.id, offer.termMonths);
                  const sc = bd ? getSiteAggregatedCosts(bd) : null;
                  const cf = contractWeightOf(bd, offer.termMonths);
                  return (
                    <td key={offer.id} className={`py-3 px-6 ${isRecommended ? 'bg-green-50/50' : ''}`}>
                      {sc ? (
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between gap-4">
                            <span className="text-yellow-700 flex items-center gap-1"><Zap className="w-3 h-3 flex-shrink-0" />Energy</span>
                            <span className="font-mono text-gray-900">{formatCurrency(sc.energy * cf)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-green-700 flex items-center gap-1"><Leaf className="w-3 h-3 flex-shrink-0" />Environ</span>
                            <span className="font-mono text-gray-900">{formatCurrency(sc.environment * cf)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-blue-700 flex items-center gap-1"><Building className="w-3 h-3 flex-shrink-0" />Network</span>
                            <span className="font-mono text-gray-900">{formatCurrency(sc.network * cf)}</span>
                          </div>
                          {sc.others > 0 && (
                            <div className="flex justify-between gap-4">
                              <span className="text-purple-700 flex items-center gap-1"><FileText className="w-3 h-3 flex-shrink-0" />Other</span>
                              <span className="font-mono text-gray-900">{formatCurrency(sc.others * cf)}</span>
                            </div>
                          )}
                        </div>
                      ) : <div className="text-sm text-gray-400">—</div>}
                    </td>
                  );
                })}
              </tr>

              {/* Green Power Percentage */}
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-6 font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-sm/50">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-green-600" />
                    Green Power
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const isBest = isBestValue(offer.id, 'green');

                  return (
                    <td
                      key={offer.id}
                      className={`py-3 px-6 text-center ${isRecommended ? 'bg-green-50/50' : ''}`}
                    >
                      <div className={`flex items-center justify-center gap-1 font-medium ${offer.greenPercent > 0 ? 'text-green-700' : 'text-gray-500'
                        }`}>
                        {offer.greenPercent}%
                        {isBest && offer.greenPercent > 0 && (
                          <Award className="w-3 h-3 text-green-600" />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-gray-100 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggleSection('rates')}>
                <td colSpan={offers.length + 1} className="py-3 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-600" />
                      <span className="font-semibold text-gray-900">Energy Rates (c/kWh)</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {expandedSections.rates ? 'Expanded' : 'Collapsed'}
                      </Badge>
                    </div>
                    {expandedSections.rates ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </td>
              </tr>

              {expandedSections.rates && (
                <>
                  {/* Peak Rates — one row per year, reads from extn.contract_terms */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`peak-${year}`} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        Peak Rate — Year {index + 1}
                      </td>
                      {offers.map((offer) => {
                        const yearIndex = index + 1;
                        const termYears = offerYearRowCount(offer);
                        if (yearIndex > termYears) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }
                        const peakRate = getContractTermRate(offer, year, 'peak', selectedRateType);
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                        const ratesForRow = offers
                          .filter(o => offerYearRowCount(o) >= yearIndex)
                          .map(o => getContractTermRate(o, year, 'peak', selectedRateType))
                          .filter(r => r > 0);
                        const minRate = ratesForRow.length > 0 ? Math.min(...ratesForRow) : 0;
                        const isBest = peakRate > 0 && peakRate === minRate;
                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'} ${isBest ? 'bg-green-50/50' : ''}`}
                          >
                            <div className={`font-mono text-sm ${isBest ? 'text-green-700 font-medium' : 'text-gray-900'}`}>
                              {peakRate > 0 ? formatCentsKwh(peakRate) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Off-Peak Rates */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`offpeak-${year}`} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        Off-Peak Rate — Year {index + 1}
                      </td>
                      {offers.map((offer) => {
                        const yearIndex = index + 1;
                        if (yearIndex > offerYearRowCount(offer)) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }
                        const offpeakRate = getContractTermRate(offer, year, 'offpeak', selectedRateType);
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                          >
                            <div className="font-mono text-sm text-gray-900">
                              {offpeakRate > 0 ? formatCentsKwh(offpeakRate) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Shoulder Rates */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`shoulder-${year}`} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        Shoulder Rate — Year {index + 1}
                      </td>
                      {offers.map((offer) => {
                        const yearIndex = index + 1;
                        if (yearIndex > offerYearRowCount(offer)) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }
                        const shoulderRate = getContractTermRate(offer, year, 'shoulder', selectedRateType);
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                          >
                            <div className="font-mono text-sm text-gray-900">
                              {shoulderRate > 0 ? formatCentsKwh(shoulderRate) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              )}

              {/* Environmental Charges Section - Collapsible */}
              <tr className="border-b border-gray-100 bg-green-50 cursor-pointer hover:bg-green-100" onClick={() => toggleSection('environmental')}>
                <td colSpan={offers.length + 1} className="py-3 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Leaf className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-gray-900">Environmental Charges</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {expandedSections.environmental ? 'Expanded' : 'Collapsed'}
                      </Badge>
                    </div>
                    {expandedSections.environmental ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </td>
              </tr>

              {expandedSections.environmental && (
                <>
                  {/* LREC */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`lrec-${year}`} className="border-b border-gray-100 hover:bg-green-50/30">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        LREC Year {index + 1} - Rate ($/kWh)
                      </td>
                      {offers.map((offer) => {
                        const yearsToDisplay = offerYearRowCount(offer);
                        const yearIndex = index + 1;
                        if (yearIndex > yearsToDisplay) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }

                        const envCharges = getOfferEnvCharges(offer);
                        const lrecData = envCharges?.LREC?.[year as keyof typeof envCharges.LREC];
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                          >
                            <div className="font-mono text-sm text-gray-900">
                              {lrecData?.ratePerKwh != null ? `$${formatEnvRate(lrecData.ratePerKwh)}` : '—'}
                            </div>
                            {lrecData?.pricePerCertificate != null && (
                              <div className="text-xs text-gray-500">${lrecData.pricePerCertificate}/cert</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* SREC */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`srec-${year}`} className="border-b border-gray-100 hover:bg-blue-50/30">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        SREC Year {index + 1} - Rate ($/kWh)
                      </td>
                      {offers.map((offer) => {
                        const yearsToDisplay = offerYearRowCount(offer);
                        const yearIndex = index + 1;
                        if (yearIndex > yearsToDisplay) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }

                        const envCharges = getOfferEnvCharges(offer);
                        const srecData = envCharges?.SREC?.[year as keyof typeof envCharges.SREC];
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                          >
                            <div className="font-mono text-sm text-gray-900">
                              {srecData?.ratePerKwh != null ? `$${formatEnvRate(srecData.ratePerKwh)}` : '—'}
                            </div>
                            {srecData?.pricePerCertificate != null && (
                              <div className="text-xs text-gray-500">${srecData.pricePerCertificate}/cert</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* VEEC/ESS/REES */}
                  {yearsToRender.map((year, index) => (
                    <tr key={`veec-${year}`} className="border-b border-gray-100 hover:bg-purple-50/30">
                      <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                        VEEC/ESS/REES Year {index + 1} - Rate ($/kWh)
                      </td>
                      {offers.map((offer) => {
                        const yearsToDisplay = offerYearRowCount(offer);
                        const yearIndex = index + 1;
                        if (yearIndex > yearsToDisplay) {
                          return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                        }

                        const envCharges = getOfferEnvCharges(offer);
                        const veecData = envCharges?.["VEEC/ESS/REES"]?.[year as keyof (typeof envCharges)["VEEC/ESS/REES"]];
                        const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                        return (
                          <td
                            key={offer.id}
                            className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                          >
                            <div className="font-mono text-sm text-gray-900">
                              {veecData?.ratePerKwh != null ? `$${formatEnvRate(veecData.ratePerKwh)}` : '—'}
                            </div>
                            {veecData?.pricePerCertificate != null && (
                              <div className="text-xs text-gray-500">${veecData.pricePerCertificate}/cert</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Additional schemes (ESC, PRC, REPS, EEIS, ...) — shown only when an offer has real data for them */}
                  {ADDITIONAL_ENV_SCHEMES.filter(s => offersHaveSchemeData(offers, s.displayKey)).flatMap(s =>
                    yearsToRender.map((year, index) => (
                      <tr key={`${s.key}-${year}`} className="border-b border-gray-100 hover:bg-amber-50/30">
                        <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                          {s.label} Year {index + 1} - Rate ($/kWh)
                        </td>
                        {offers.map((offer) => {
                          const yearsToDisplay = offerYearRowCount(offer);
                          const yearIndex = index + 1;
                          if (yearIndex > yearsToDisplay) {
                            return <td key={offer.id} className="py-2 px-6 bg-gray-50/50"></td>;
                          }

                          const envCharges = getOfferEnvCharges(offer);
                          const schemeData = envCharges?.[s.displayKey]?.[year];
                          const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                          return (
                            <td
                              key={offer.id}
                              className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                            >
                              <div className="font-mono text-sm text-gray-900">
                                {schemeData?.ratePerKwh != null ? `$${formatEnvRate(schemeData.ratePerKwh)}` : '—'}
                              </div>
                              {schemeData?.pricePerCertificate != null && (
                                <div className="text-xs text-gray-500">${schemeData.pricePerCertificate}/cert</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </>
              )}

              {/* Non-Energy Charges Section - Collapsible */}
              <tr className="border-b border-gray-100 bg-purple-50 cursor-pointer hover:bg-purple-100" onClick={() => toggleSection('nonEnergy')}>
                <td colSpan={offers.length + 1} className="py-3 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-gray-900">Non-Energy Charges ($ P.A.)</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {expandedSections.nonEnergy ? 'Expanded' : 'Collapsed'}
                      </Badge>
                    </div>
                    {expandedSections.nonEnergy ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </td>
              </tr>

              {expandedSections.nonEnergy && (
                <>
                  {/* Meter Agreement */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                      Meter Agreement
                    </td>
                    {offers.map((offer) => {
                      const rateData = getRateData(offer);
                      const meterAgreement = rateData?.nonEnergyCharges?.["Meter Agreement"] || 0;
                      const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                      return (
                        <td
                          key={offer.id}
                          className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                        >
                          <span className="font-mono text-sm text-gray-900">
                            {meterAgreement > 0 ? formatCurrency(meterAgreement) : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Supplementary Metering */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                      Supplementary Metering
                    </td>
                    {offers.map((offer) => {
                      const rateData = getRateData(offer);
                      const supplementaryMetering = rateData?.nonEnergyCharges?.["Supplementary Metering"] || 0;
                      const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                      return (
                        <td
                          key={offer.id}
                          className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                        >
                          <span className="font-mono text-sm text-gray-900">
                            {supplementaryMetering > 0 ? formatCurrency(supplementaryMetering) : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Other Service Charges */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="py-2 px-6 pl-10 text-gray-700 sticky left-0 bg-white z-10 text-sm">
                      Other Service Charge
                    </td>
                    {offers.map((offer) => {
                      const rateData = getRateData(offer);
                      const otherCharges = Object.entries(rateData?.nonEnergyCharges || {})
                        .filter(([key]) => key !== "Meter Agreement" && key !== "Supplementary Metering")
                        .reduce((sum, [, value]) => sum + (typeof value === 'number' ? value : 0), 0);
                      const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;

                      return (
                        <td
                          key={offer.id}
                          className={`py-2 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                        >
                          <span className="font-mono text-sm text-gray-900">
                            {otherCharges > 0 ? formatCurrency(otherCharges) : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </>
              )}

              {/* Green Energy - Always Expanded */}
              <tr className="border-b-2 border-gray-200 bg-gray-50">
                <td className="py-4 px-6 font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    Green Energy %
                  </div>
                </td>
                {offers.map((offer) => {
                  const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
                  const isBest = isBestValue(offer.id, 'green');

                  return (
                    <td
                      key={offer.id}
                      className={`py-4 px-6 text-center ${isRecommended ? 'bg-green-50' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className={`font-semibold ${isBest ? 'text-green-600' : 'text-gray-900'}`}>
                          {offer.greenPercent}%
                        </span>
                        {isBest && (
                          <Award className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Cards View Component
interface ComparisonCardsViewProps {
  offers: Offer[];
  retailers: Retailer[];
  getRetailerInfo: (offer: Offer) => Retailer | null;
  getRateData: (offer: Offer, rateType?: 'smooth' | 'stepped') => any;
  formatCurrency: (amount: number) => string;
  formatRate: (rate: number) => string;
  formatPercent: (percent: number) => string;
  handleSelectOffer: (offerId: string) => void;
  isBestValue: (offerId: string, type: 'cost' | 'savings' | 'green') => boolean;
  getDisplayRank: (offerId: string, termMonths: number) => number | null;
  selectedSite: any;
  getCalculatedAnnualCost: (offerId: string, termMonths?: number) => number | null;
  getCalculatedBreakdown: (offerId: string, termMonths: number) => ContractCostBreakdown | null;
  isLoadingCosts: boolean;
  selectedRateType: 'Smooth' | 'Stepped';
}

const ComparisonCardsView: React.FC<ComparisonCardsViewProps> = ({
  offers,
  retailers,
  getRetailerInfo,
  getRateData,
  formatCurrency,
  formatRate,
  formatPercent,
  handleSelectOffer,
  isBestValue,
  getDisplayRank,
  selectedSite,
  getCalculatedAnnualCost,
  getCalculatedBreakdown,
  isLoadingCosts,
  selectedRateType,
}) => {
  // Initialize all cards as expanded by default
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    offers.forEach(offer => {
      initial[offer.id] = true;
    });
    return initial;
  });

  // Update expandedCards when offers change
  useEffect(() => {
    setExpandedCards(prev => {
      const updated = { ...prev };
      offers.forEach(offer => {
        if (!(offer.id in updated)) {
          updated[offer.id] = true; // Default to expanded
        }
      });
      return updated;
    });
  }, [offers]);

  const toggleCardExpansion = (offerId: string): void => {
    setExpandedCards(prev => ({
      ...prev,
      [offerId]: !prev[offerId],
    }));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
      {offers.map((offer) => {
        const retailer = getRetailerInfo(offer);
        const offerRank = getDisplayRank(offer.id, offer.termMonths ?? 0);
        const isRank1 = offerRank === 1;
        const isRank2 = offerRank === 2;
        const isRank3 = offerRank === 3;
        const rateData = getRateData(offer);
        const isExpanded = expandedCards[offer.id] || false;
        const isBestCost = isBestValue(offer.id, 'cost');
        const isBestSavings = isBestValue(offer.id, 'savings');
        const isBestGreen = isBestValue(offer.id, 'green');

        return (
          <Card
            key={offer.id}
            className={`relative overflow-hidden transition-all duration-200 ${
              isRank1 ? 'border-2 border-green-500 shadow-md shadow-green-500/20' :
              isRank2 ? 'border-2 border-blue-400 shadow-md shadow-blue-400/20' :
              isRank3 ? 'border-2 border-purple-400 shadow-md shadow-purple-400/20' :
              'border border-gray-200 hover:shadow-sm'
            }`}
          >
            {/* Rank Badge */}
            {isRank1 && (
              <div className="absolute top-0 right-0 bg-green-600 text-white px-2 py-0.5 rounded-bl-lg text-[10px] font-semibold flex items-center gap-0.5 z-10">
                <Star className="w-2.5 h-2.5 fill-current" />
                1st
              </div>
            )}
            {isRank2 && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-2 py-0.5 rounded-bl-lg text-[10px] font-semibold flex items-center gap-0.5 z-10">
                2nd
              </div>
            )}
            {isRank3 && (
              <div className="absolute top-0 right-0 bg-purple-500 text-white px-2 py-0.5 rounded-bl-lg text-[10px] font-semibold flex items-center gap-0.5 z-10">
                3rd
              </div>
            )}

            <CardHeader className={`pb-2 pt-3 px-3 ${isRank1 ? 'bg-green-50' : isRank2 ? 'bg-blue-50' : isRank3 ? 'bg-purple-50' : 'bg-white'}`}>
              {/* Single select button — click order sets rank */}
              <Button
                className={`w-full mb-3 h-8 text-xs font-semibold ${
                  isRank1 ? 'bg-green-600 hover:bg-green-700 text-white' :
                  isRank2 ? 'bg-blue-500 hover:bg-blue-600 text-white' :
                  isRank3 ? 'bg-purple-500 hover:bg-purple-600 text-white' :
                  'border-2 border-gray-300 hover:border-primary hover:bg-primary/5'
                }`}
                variant={offerRank ? "default" : "outline"}
                onClick={() => handleSelectOffer(offer.id)}
              >
                {isRank1 && <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Recommended</>}
                {isRank2 && <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />2nd Choice</>}
                {isRank3 && <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />3rd Choice</>}
                {!offerRank && <><Star className="w-3.5 h-3.5 mr-1.5" />Select Offer</>}
              </Button>

              {/* Retailer Logo & Name */}
              <div className="flex flex-col items-center gap-1.5 mb-2">
                {retailer?.logo || offer.retailerLogo ? (
                  <div className="w-10 h-10 rounded-md bg-white border border-gray-200 flex items-center justify-center p-1">
                    <img
                      src={retailer?.logo || offer.retailerLogo}
                      alt={retailer?.name || offer.retailerName}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">
                      {(retailer?.name || offer.retailerName || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="text-center">
                  <div className="font-semibold text-gray-900 text-xs leading-tight">
                    {retailer?.name || offer.retailerName}
                  </div>
                  {(() => {
                    const { actual, isFallback } = getOfferRateTypeInfo(offer, selectedRateType);
                    if (!actual) return null;
                    return (
                      <span
                        title={isFallback ? `${selectedRateType} not available — showing ${actual}` : `${actual} rates`}
                        className={`inline-flex items-center gap-0.5 mt-1 text-[9px] font-semibold px-2 py-0.5 rounded-full border ${
                          actual === 'Smooth'
                            ? 'bg-blue-50 text-blue-700 border-blue-300'
                            : 'bg-amber-50 text-amber-700 border-amber-300'
                        }`}
                      >
                        {actual}{isFallback && <span className="opacity-60 ml-0.5">*</span>}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Annual Cost - Highlighted - USE REAL CALCULATED COSTS */}
              {(() => {
                const calculatedCost = getCalculatedAnnualCost(offer.id, offer.termMonths);
                const displayCost = calculatedCost ?? offer.estimatedAnnualCost;
                const breakdown = getCalculatedBreakdown(offer.id, offer.termMonths);
                const sc = breakdown ? getSiteAggregatedCosts(breakdown) : null;

                return (
                  <div className="text-center py-2 bg-blue-50 rounded-md border border-blue-200">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <span className="text-[10px] text-gray-600 font-medium">Annual Cost</span>
                      {isBestCost && !isLoadingCosts && <Award className="w-3 h-3 text-green-600" />}
                    </div>
                    {isLoadingCosts ? (
                      <div className="flex items-center justify-center py-1">
                        <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                      </div>
                    ) : (
                      <>
                        <div className={`text-xl font-bold leading-tight ${isBestCost ? 'text-green-600' : 'text-gray-900'}`}>
                          {displayCost ? formatCurrency(displayCost) : '—'}
                        </div>
                        {sc && sc.annualCurrentCost > 0 && (
                          <div className="text-[9px] text-gray-500 mt-0.5">
                            Current: {formatCurrency(sc.annualCurrentCost)}/yr
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Rank + Savings vs Current Contract */}
              {(() => {
                const breakdown = getCalculatedBreakdown(offer.id, offer.termMonths);
                if (!breakdown) return null;
                const sc = getSiteAggregatedCosts(breakdown);
                const displayRank = getDisplayRank(offer.id, offer.termMonths);
                return (
                  <div className="space-y-1.5 mt-1">
                    {displayRank != null && (
                      <div className="flex items-center justify-center">
                        <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                          displayRank === 1 ? 'bg-green-100 text-green-700 border border-green-300' :
                          displayRank === 2 ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                          'bg-gray-100 text-gray-600 border border-gray-300'
                        }`}>
                          #{displayRank} {displayRank === 1 ? '· Best Value' : displayRank === 2 ? '· 2nd Best' : '· Competitive'}
                        </span>
                      </div>
                    )}
                    {sc && sc.annualSavings !== 0 && (
                      <div className={`text-center px-2 py-1 rounded-md text-[10px] font-medium ${
                        sc.annualSavings > 0
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-600 border border-red-200'
                      }`}>
                        {sc.annualSavings > 0 ? '↓ Save ' : '↑ Cost +'}
                        {formatCurrency(Math.abs(sc.annualSavings))}/yr
                        {' '}({Math.abs(sc.savingsPercent).toFixed(1)}% vs current)
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardHeader>

            <CardContent className="space-y-2 px-3 pb-3">
              {/* Cost Breakdown Bar - Always Visible */}
              {(() => {
                const breakdown = getCalculatedBreakdown(offer.id, offer.termMonths);
                if (!breakdown) return null;
                const sc = getSiteAggregatedCosts(breakdown);
                if (!sc) return null;
                const contractFraction = contractWeightOf(breakdown, offer.termMonths);
                const contractTotal = sc.annualOfferCost * contractFraction;
                const contractEnergy = sc.energy * contractFraction;
                const contractEnv = sc.environment * contractFraction;
                const contractNetwork = sc.network * contractFraction;
                const contractOthers = sc.others * contractFraction;
                const items = [
                  { label: 'Energy', val: contractEnergy, color: 'bg-yellow-400' },
                  { label: 'Environ', val: contractEnv, color: 'bg-green-400' },
                  { label: 'Network', val: contractNetwork, color: 'bg-blue-400' },
                  { label: 'Other', val: contractOthers, color: 'bg-purple-400' },
                ];
                return (
                  <div className="bg-gray-50 rounded-md p-2 border border-gray-200">
                    <div className="text-[9px] font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                      <BarChart3 className="w-3 h-3 text-gray-500" />
                      Cost Breakdown ({formatTermLabel(offer.termMonths)})
                    </div>
                    <div className="space-y-1">
                      {items.map(({ label, val, color }) => (
                        <div key={label} className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${color} flex-shrink-0`} />
                          <span className="text-[8px] text-gray-600 w-10 flex-shrink-0">{label}</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`${color} h-1.5 rounded-full`}
                              style={{ width: `${Math.round((val / (contractTotal || 1)) * 100)}%` }}
                            />
                          </div>
                          <span className="text-[8px] font-mono text-gray-900 w-14 text-right flex-shrink-0">
                            {formatCurrency(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1.5 pt-1 border-t border-gray-200 flex justify-between items-center">
                      <span className="text-[8px] text-gray-500">Total ({formatTermLabel(offer.termMonths)})</span>
                      <span className="text-[9px] font-bold text-gray-900">{formatCurrency(contractTotal)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Contract Term - Static Display */}
              <div className="space-y-1">
                <div className="text-[10px] font-medium text-gray-700">Contract Term:</div>
                <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <Calendar className="w-3 h-3 text-gray-500" />
                  <span className="font-medium text-gray-900 text-xs">
                    {formatTermLabel(offer.termMonths)}
                  </span>
                </div>
              </div>

              {/* Green Energy */}
              <div className="flex items-center justify-between p-1.5 bg-green-50 rounded-md border border-green-200">
                <div className="flex items-center gap-1">
                  <Leaf className="w-3 h-3 text-green-600" />
                  <span className="text-[10px] font-medium text-gray-700">Green</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`font-semibold text-xs ${isBestGreen ? 'text-green-600' : 'text-gray-900'}`}>
                    {offer.greenPercent}%
                  </span>
                  {isBestGreen && <Award className="w-3 h-3 text-green-600" />}
                </div>
              </div>

              {/* Expandable Details */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleCardExpansion(offer.id)}
                className="w-full flex items-center justify-between h-7 text-xs"
              >
                <span className="text-[10px]">{isExpanded ? 'Hide' : 'Show'} Details</span>
                {isExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </Button>

              {isExpanded && (
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  {/* Energy Rates — from extn.contract_terms for this specific term */}
                  <div className="bg-yellow-50 rounded-md p-2 border border-yellow-200">
                    <h4 className="text-[10px] font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                      <Zap className="w-3 h-3 text-yellow-600" />
                      Energy Rates
                      {getOfferRateTypeInfo(offer, selectedRateType).actual && (
                        <span className="ml-1 text-[8px] text-gray-500 font-normal">
                          ({getOfferRateTypeInfo(offer, selectedRateType).actual})
                        </span>
                      )}
                    </h4>
                    {(() => {
                      const yearKeys = getTermYearKeys(offer, selectedRateType);
                      if (yearKeys.length === 0) {
                        return <div className="text-[9px] text-gray-400 italic">No rate data available</div>;
                      }
                      return (
                        <div className="space-y-1.5">
                          {/* Peak */}
                          <div>
                            <div className="text-[9px] font-medium text-gray-700 mb-1">Peak</div>
                            <div className="grid grid-cols-3 gap-1">
                              {yearKeys.map((year) => {
                                const rate = getContractTermRate(offer, year, 'peak', selectedRateType);
                                return (
                                  <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                    <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                    <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                      {rate > 0 ? formatCentsKwh(rate) : '—'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* Off-Peak */}
                          <div>
                            <div className="text-[9px] font-medium text-gray-700 mb-1">Off-Peak</div>
                            <div className="grid grid-cols-3 gap-1">
                              {yearKeys.map((year) => {
                                const rate = getContractTermRate(offer, year, 'offpeak', selectedRateType);
                                return (
                                  <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                    <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                    <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                      {rate > 0 ? formatCentsKwh(rate) : '—'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* Shoulder */}
                          <div>
                            <div className="text-[9px] font-medium text-gray-700 mb-1">Shoulder</div>
                            <div className="grid grid-cols-3 gap-1">
                              {yearKeys.map((year) => {
                                const rate = getContractTermRate(offer, year, 'shoulder', selectedRateType);
                                return (
                                  <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                    <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                    <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                      {rate > 0 ? formatCentsKwh(rate) : '—'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Environmental Charges - All 6 Years */}
                  <div className="bg-green-50 rounded-md p-2 border border-green-200">
                    <h4 className="text-[10px] font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                      <Leaf className="w-3 h-3 text-green-600" />
                      Environmental
                    </h4>
                    <div className="space-y-1.5">
                      {(() => {
                        const envCharges = getOfferEnvCharges(offer);
                        const termYearKeys = getTermYearKeys(offer, selectedRateType);
                        return (
                          <>
                            {/* LREC */}
                            <div>
                              <div className="text-[9px] font-medium text-gray-700 mb-1">LREC</div>
                              <div className="grid grid-cols-3 gap-1">
                                {termYearKeys.map((year) => {
                                  const lrecData = envCharges?.LREC?.[year as keyof typeof envCharges.LREC];
                                  return (
                                    <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                      <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                      <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                        {lrecData?.ratePerKwh != null ? `$${formatEnvRate(lrecData.ratePerKwh)}` : '—'}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* SREC */}
                            <div>
                              <div className="text-[9px] font-medium text-gray-700 mb-1">SREC</div>
                              <div className="grid grid-cols-3 gap-1">
                                {termYearKeys.map((year) => {
                                  const srecData = envCharges?.SREC?.[year as keyof typeof envCharges.SREC];
                                  return (
                                    <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                      <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                      <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                        {srecData?.ratePerKwh != null ? `$${formatEnvRate(srecData.ratePerKwh)}` : '—'}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* VEEC/ESS/REES */}
                            <div>
                              <div className="text-[9px] font-medium text-gray-700 mb-1">VEEC</div>
                              <div className="grid grid-cols-3 gap-1">
                                {termYearKeys.map((year) => {
                                  const veecData = envCharges?.["VEEC/ESS/REES"]?.[year as keyof (typeof envCharges)["VEEC/ESS/REES"]];
                                  return (
                                    <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                      <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                      <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                        {veecData?.ratePerKwh != null ? `$${formatEnvRate(veecData.ratePerKwh)}` : '—'}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Additional schemes (ESC, PRC, REPS, EEIS, ...) — shown only when this offer has real data for them */}
                            {ADDITIONAL_ENV_SCHEMES.filter(s => offersHaveSchemeData([offer], s.displayKey)).map(s => (
                              <div key={s.key}>
                                <div className="text-[9px] font-medium text-gray-700 mb-1">{s.label}</div>
                                <div className="grid grid-cols-3 gap-1">
                                  {termYearKeys.map((year) => {
                                    const schemeData = envCharges?.[s.displayKey]?.[year];
                                    return (
                                      <div key={year} className="bg-white rounded p-1 border border-gray-200">
                                        <div className="text-gray-500 text-[8px]">Y{year.replace('year_', '')}</div>
                                        <div className="font-mono text-gray-900 text-[9px] leading-tight">
                                          {schemeData?.ratePerKwh != null ? `$${formatEnvRate(schemeData.ratePerKwh)}` : '—'}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Non-Energy Charges */}
                  <div className="bg-purple-50 rounded-md p-2 border border-purple-200">
                    <h4 className="text-[10px] font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                      <Building className="w-3 h-3 text-purple-600" />
                      Non-Energy
                    </h4>
                    <div className="space-y-1 text-[9px]">
                      {rateData?.nonEnergyCharges?.["Meter Agreement"] !== undefined && (
                        <div className="flex justify-between items-center bg-white rounded p-1 border border-gray-200">
                          <span className="text-gray-700 text-[9px]">Meter:</span>
                          <span className="font-mono text-gray-900 text-[9px]">
                            {rateData.nonEnergyCharges["Meter Agreement"] > 0
                              ? formatCurrency(rateData.nonEnergyCharges["Meter Agreement"])
                              : '—'}
                          </span>
                        </div>
                      )}
                      {rateData?.nonEnergyCharges?.["Supplementary Metering"] !== undefined && (
                        <div className="flex justify-between items-center bg-white rounded p-1 border border-gray-200">
                          <span className="text-gray-700 text-[9px]">Supp:</span>
                          <span className="font-mono text-gray-900 text-[9px]">
                            {rateData.nonEnergyCharges["Supplementary Metering"] > 0
                              ? formatCurrency(rateData.nonEnergyCharges["Supplementary Metering"])
                              : '—'}
                          </span>
                        </div>
                      )}
                      {(() => {
                        const otherCharges = Object.entries(rateData?.nonEnergyCharges || {})
                          .filter(([key]) => key !== "Meter Agreement" && key !== "Supplementary Metering")
                          .reduce((sum, [, value]) => sum + (typeof value === 'number' ? value : 0), 0);
                        return otherCharges > 0 ? (
                          <div className="flex justify-between items-center bg-white rounded p-1 border border-gray-200">
                            <span className="text-gray-700 text-[9px]">Other:</span>
                            <span className="font-mono text-gray-900 text-[9px]">
                              {formatCurrency(otherCharges)}
                            </span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default CompareAndSelectStep;
